/**
 * Membership Cart Logic
 * 
 * This file handles the automatic addition of membership products when
 * a user selects a member price variant, and shows a popup for non-member variants.
 * 
 * COMMENTED OUT FEATURES (to prevent unnecessary operations):
 * - Periodic cart monitoring (setupCartMonitoring)
 * - Cart change event handling (handleCartChange)
 * - Automatic membership addition for member variants (handleMemberVariantAdd)
 * - General cart maintenance checks (checkAndMaintainMembershipProducts)
 * 
 * ACTIVE FEATURES:
 * - Automatic membership addition for non-member variants
 * - Cart page popup detection and membership addition
 * - Variant conversion from regular to member variants (when adding membership)
 * - Variant conversion from member to regular variants (when removing membership)
 * - Page refresh after cart operations (on cart page)
 */

class MembershipCartManager {
  constructor() {
    // Debug flag - set to false to reduce console noise
    this.DEBUG = false;
    // Toggle to control automatic membership additions (manual adds bypass this)
    this.autoAddMembershipEnabled = false;
    
    this.membershipProducts = {
      gold: {
        productId: null,
        variantId: null,
        tier: 'Gold'
      },
      platinum: {
        productId: null,
        variantId: null,
        tier: 'Platinum'
      }
    };
    
    // Initialize membership products from theme settings
    this.initializeMembershipProducts();
    
    this.currentMemberVariant = null;
    this.currentProductHandle = null;
    this.lastCartCheck = 0;
    this.cartCheckInterval = 5000; // 5 seconds
    this.isProcessing = false;
    this.isProcessingMembershipProduct = null;
    this.isEnsuringMembership = false;
    this.suppressNextCartReload = false;
    this.membershipReloadTimeout = null;
    this.pendingReloadReason = null;
    
    // NEW: Prevent multiple simultaneous conversions
    this.isConvertingVariants = false;
    this.manualMembershipAddInProgress = false;

    // Customer status (supports either window.customerInfo or themeSettings.customer)
    this.customerStatus = {
      isLoggedIn: false,
      isActiveMember: false,
      customerTags: []
    };
    
    this.init();
  }

  log(...args) {
    if (this.DEBUG) console.log('[Membership]', ...args);
  }
  
  init() {
    if (this.DEBUG) console.log('MembershipCartManager initialized');
    
    // Initialize customer status from globals
    try {
      if (window.customerInfo) {
        this.customerStatus.isLoggedIn = !!window.customerInfo.isLoggedIn;
        this.customerStatus.isActiveMember = !!window.customerInfo.isActiveMember;
        this.customerStatus.customerTags = Array.isArray(window.customerInfo.customerTags) ? window.customerInfo.customerTags : [];
      } else if (window.themeSettings && window.themeSettings.customer) {
        const c = window.themeSettings.customer;
        this.customerStatus.isLoggedIn = !!c.isLoggedIn;
        this.customerStatus.isActiveMember = !!c.isActiveMember;
        this.customerStatus.customerTags = Array.isArray(c.customerTags) ? c.customerTags : [];
      }
      if (this.DEBUG) console.log('Customer status:', this.customerStatus);
    } catch (e) {
      console.warn('Unable to read customer status from theme settings:', e);
    }

    // Listen for variant changes to detect member price variants
    document.addEventListener('variant:change', this.handleVariantChange.bind(this));
    
    // Listen for add to cart events to intercept the process
    document.addEventListener('variant:add', this.handleVariantAdd.bind(this));
    
    // Listen for cart changes to detect empty cart and clean up cookies
    document.addEventListener('cart:change', this.handleCartChange.bind(this));
    
    // Listen for cart item removal events (more immediate detection)
    document.addEventListener('cart:item-removed', this.handleCartItemRemoved.bind(this));
    
    // Also listen for any cart updates that might indicate item removal
    document.addEventListener('cart:updated', this.handleCartUpdated.bind(this));
    
    // Listen for clicks on remove buttons (more direct detection)
    document.addEventListener('click', this.handleRemoveButtonClick.bind(this));

    // Listen for membership plan removal specifically
    document.addEventListener('click', this.handleMembershipRemovalClick.bind(this));

    // Listen for checkout clicks to handle post-login cleanup
    document.addEventListener('click', this.handleCheckoutClick.bind(this));
    
    // Listen for page visibility changes to pause/resume monitoring
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    // Listen for page unload to clean up resources
    window.addEventListener('beforeunload', this.cleanup.bind(this));
    
    // Listen for login events to check if active member needs membership plan removed
    this.setupLoginDetection();
    
    // Setup OTP integration for immediate login detection
    this.setupOtpIntegration();
    
    // Try to get the current product handle on initialization
    this.currentProductHandle = this.getCurrentProductHandle();
    if (this.currentProductHandle) {
      console.log('Current product handle:', this.currentProductHandle);
    }
    
    // Set up DOM monitoring for cart changes (fallback detection)
    this.setupCartDOMMonitoring();
    
    // Set up periodic monitoring for auto-removal (more aggressive)
    this.setupPeriodicAutoRemovalCheck();
    
    // Check if we need to reopen cart drawer after reload
    this.checkAndOpenCartDrawerAfterReload();
    
    // Listen for cart refresh to update variant visibility on product pages
    document.addEventListener('cart:refresh', () => {
      if (window.location.pathname.includes('/products/')) {
        this.updateVariantOptionsVisibility();
      }
    });
    
    // Initial variant visibility check on product pages
    if (window.location.pathname.includes('/products/')) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        this.updateVariantOptionsVisibility();
      }, 500);
    }
    
    // Check if we're on cart page and need to show popup
    if (window.location.pathname === '/cart') {
      console.log('🛒 Cart page detected - initializing cart page checks');
      // Check if we have a URL parameter indicating recent add to cart
      const urlParams = new URLSearchParams(window.location.search);
      const addedToCart = urlParams.get('added');
      
      if (addedToCart) {
        console.log('✅ Detected recent add to cart (?added parameter), checking for popup in 500ms');
        // Small delay to ensure cart is updated
        setTimeout(() => {
          this.checkCartPageForPopup();
        }, 500);
      } else {
        console.log('✅ Regular cart page load, checking for popup immediately');
        // Regular cart page load, still check for popup
        this.checkCartPageForPopup();
      }
    } else {
      console.log('ℹ️  Not on cart page (pathname:', window.location.pathname, ') - skipping cart page checks');
    }
  }

  /**
   * Handle variant changes to detect when member price variants are selected
   */
  handleVariantChange(event) {
    this.log('Variant change event:', event.detail);
    
    const variant = event.detail.variant;
    if (!variant) {
      return;
    }

    // If active member, hide normal variants in UI (basic DOM approach)
    if (this.customerStatus.isLoggedIn && this.customerStatus.isActiveMember) {
      try {
        // Hide options that look like non-member
        document.querySelectorAll('option, [role="option"]').forEach(el => {
          const txt = (el.textContent || '').toLowerCase();
          if (txt.includes('non-member') || txt.includes('regular') || txt.includes('non member')) {
            el.style.display = 'none';
          }
        });
        document.querySelectorAll('.variant-label, .price, .badge').forEach(el => {
          const txt = (el.textContent || '').toLowerCase();
          if (txt.includes('non-member') || txt.includes('regular price')) {
            el.style.display = 'none';
          }
        });
      } catch (e) {
        if (this.DEBUG) console.warn('Failed to hide normal variants for active member:', e);
      }
    }

    // Check if this is a member price variant
    if (this.isMemberPriceVariant(variant)) {
      this.log('Member price variant selected:', variant.title);
      this.currentMemberVariant = variant;
    } else {
      this.currentMemberVariant = null;
    }
  }

  /**
   * Handle when a variant is added to cart
   */
  async handleVariantAdd(event) {
    console.log('Variant add event:', event.detail);
    
    const items = event.detail.items;
    if (!items) {
      console.log('No items in event');
      return;
    }

    // Check if membership plan was manually added back
    const membershipItems = items.filter(item => {
      const productTitle = (item.product_title || '').toLowerCase();
      return productTitle.includes('plan') || 
             productTitle.includes('gold') || 
             productTitle.includes('platinum') ||
             productTitle.includes('membership');
    });

    // Check if any of the added items are member price variants
    const memberItems = items.filter(item => {
      const variantTitle = item.variant_title || '';
      return variantTitle.toLowerCase().includes('member') || 
             variantTitle.toLowerCase().includes('beauty pie+');
    });

    if (memberItems.length > 0) {
      this.log('Member price variants added:', memberItems);
      // Use cart from event when available to avoid an extra /cart.js fetch (faster drawer update)
      const cartFromEvent = event.detail?.cart;
      await this.handleMemberVariantAdd(memberItems, cartFromEvent);
    } else {
      this.log('Non-member variants added:', items);
      // Handle non-member variant addition
      await this.handleNonMemberVariantAdd(items);
    }
  }

  /**
   * Handle when member price variants are added to cart
   * @param {Array} memberItems - Items added (from variant:add event)
   * @param {Object} [cartFromEvent] - Optional cart from event.detail.cart to avoid extra fetch
   */
  async handleMemberVariantAdd(memberItems, cartFromEvent) {
    this.log('Processing member variant addition:', memberItems);
    
    // If logged-in active member, do nothing (they already have membership)
    if (this.customerStatus.isLoggedIn && this.customerStatus.isActiveMember) {
      this.log('Active member detected - skipping member variant handling');
      return;
    }
    
    // Use cart from event when available to avoid extra round-trip (faster cart drawer update)
    let cart;
    if (cartFromEvent && Array.isArray(cartFromEvent.items)) {
      cart = cartFromEvent;
      this.log('Using cart from variant:add event (skip fetch)');
    } else {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        console.error('Failed to fetch cart');
        return;
      }
      cart = await cartResponse.json();
    }
    
    const membershipExists = cart.items.some(item => 
      item.product_id === this.membershipProducts.gold.productId ||
      item.product_id === this.membershipProducts.platinum.productId
    );
    
    if (membershipExists) {
      console.log('Membership already in cart - no need to add, just adding product normally');
      return; // Exit - membership already exists, just add the product ✅
    }
    
    // For non-active members (including logged in but not active), ensure membership is added
    this.log('Non-active member with member variants - ensuring membership is added');
    
    // Get the highest tier required for the added member variants
    let highestTier = 'Gold';
    
    for (const item of memberItems) {
      const productHandle = item.handle;
      const tier = this.getProductMembershipTier(productHandle);
      
      if (tier === 'Platinum') {
        highestTier = 'Platinum';
        break;
      }
    }
    
    this.log('Highest tier required:', highestTier);
    
    // Ensure the appropriate membership product is in cart (force add for member variants)
    // Pass cart data and skip conversion to speed up the process
    await this.ensureMembershipProductInCart(highestTier, { 
      force: true, 
      skipConversion: true, 
      skipRefresh: true,
      cartData: cart 
    });
    
    // Refresh cart immediately so drawer updates with membership (single refresh)
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    this.log('Cart refreshed to show membership savings');
  }

  /**
   * Handle when non-member variants are added to cart
   */
  async handleNonMemberVariantAdd(items) {
    console.log('Processing non-member variant addition:', items);
    
    // If logged-in active member, do nothing (no auto-adding, no popup)
    if (this.customerStatus.isLoggedIn && this.customerStatus.isActiveMember) {
      console.log('Active member detected - skipping non-member add handling');
      return;
    }
    
    // Check if membership is already in cart
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    const existingMembership = cart.items.find(item => 
      item.product_title && (
        item.product_title.toLowerCase().includes('plan') ||
        item.product_title.toLowerCase().includes('gold') ||
        item.product_title.toLowerCase().includes('platinum')
      )
    );
    
    if (!existingMembership) {
      console.log('No membership in cart - auto-add disabled for non-member variants');
      return;
    } else {
      console.log('Membership already in cart - ensuring manager refresh');
      document.dispatchEvent(new CustomEvent('membership:manual-add-complete', {
        detail: { source: 'non-member-variant-add' }
      }));
    }
  }

  /**
   * Show the membership popup
   */
  showMembershipPopup() {
    // DISABLED: Popup functionality disabled - membership products are still added automatically
    console.log('🎯 Membership popup is disabled');
    return;
    
  }



  /**
   * Calculate actual membership savings (member price vs regular price)
   */
  async calculateMembershipSavings() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      let totalSavings = 0;
      
      for (const item of cart.items) {
        // Skip membership products
        if (item.product_title && (
          item.product_title.toLowerCase().includes('plan') ||
          item.product_title.toLowerCase().includes('gold') ||
          item.product_title.toLowerCase().includes('platinum')
        )) {
          continue;
        }
        
        // Check if this is a member variant
        const variantTitle = item.variant_title || '';
        const isMemberVariant = variantTitle.toLowerCase().includes('member')
        
        if (isMemberVariant) {
          // This is a member variant, find corresponding regular variant price
          try {
            const productResponse = await fetch(`/products/${item.handle}.js`);
            const product = await productResponse.json();
            
            const regularVariant = product.variants.find(variant => {
              const vt = (variant.title || '').toLowerCase();
              return !vt.includes('member')
            });
            
            if (regularVariant && regularVariant.price > item.price) {
              const itemSavings = (regularVariant.price - item.price) * item.quantity;
              totalSavings += itemSavings;
              console.log(`Savings for ${item.title}: ${itemSavings} (${regularVariant.price} - ${item.price}) * ${item.quantity}`);
            }
          } catch (error) {
            console.error('Error fetching product data for savings:', error);
          }
        }
      }
      
      console.log('Total calculated savings:', totalSavings);
      return totalSavings;
    } catch (error) {
      console.error('Error calculating membership savings:', error);
      return 0;
    }
  }

  /**
   * Find member variant price for a cart item
   */
  async findMemberVariantPrice(item) {
    try {
      const productResponse = await fetch(`/products/${item.handle}.js`);
      const product = await productResponse.json();
      
      // Find member variant
      const memberVariant = product.variants.find(variant => {
        const variantTitle = variant.title || '';
        return variantTitle.toLowerCase().includes('member')
      });
      
      return memberVariant ? memberVariant.price : null;
    } catch (error) {
      console.error('Error fetching product data:', error);
      // Fallback to estimated discount
      return item.price * 0.8; // Assume 20% discount
    }
  }

  /**
   * Handle cart changes to detect empty cart and clean up cookies
   */
  handleCartChange(event) {
    console.log('🔄 Cart change event:', event.detail);
    
    // Check if cart is empty and clean up cookies if needed
    this.checkForEmptyCartAndCleanup();
    
    // Update variant visibility on product pages when cart changes
    if (window.location.pathname.includes('/products/')) {
      setTimeout(() => {
        this.updateVariantOptionsVisibility();
      }, 600);
    }
    
    if (event.detail.baseEvent === 'variant:add') {
      // Cart was updated, check if we need to maintain membership products
      // Add a small delay to prevent race conditions
      setTimeout(async () => {
        await this.ensureMembershipProductInCart();
      }, 500);
    }
  }

  /**
   * Handle cart item removal events
   */
  handleCartItemRemoved(event) {
    console.log('🗑️ Cart item removed event:', event.detail);
    
    // Add a small delay to allow cart to update, then check for auto-removal
    setTimeout(() => {
      this.checkForEmptyCartAndCleanup();
    }, 500);
    
    // Update variant visibility on product pages when items are removed
    if (window.location.pathname.includes('/products/')) {
      setTimeout(() => {
        this.updateVariantOptionsVisibility();
      }, 700);
    }
  }

  /**
   * Handle general cart updates
   */
  handleCartUpdated(event) {
    console.log('📦 Cart updated event:', event.detail);
    
    // Add a small delay to allow cart to update, then check for auto-removal
    setTimeout(() => {
      this.checkForEmptyCartAndCleanup();
    }, 500);
    
    // Update variant visibility on product pages when cart is updated
    if (window.location.pathname.includes('/products/')) {
      setTimeout(() => {
        this.updateVariantOptionsVisibility();
      }, 700);
    }
  }

  /**
   * Handle clicks on remove buttons (more direct detection)
   */
  handleRemoveButtonClick(event) {
    // Check if clicked element is a remove button or contains one
    const removeButton = event.target.closest('a[href*="remove"], button[data-action="remove"], .remove, [aria-label*="remove"], [title*="remove"]');
    
    if (removeButton) {
      console.log('🗑️ Remove button clicked:', removeButton);
      
      // Add a longer delay to allow the removal to complete
      setTimeout(() => {
        console.log('🔄 Checking cart after remove button click...');
        this.checkForEmptyCartAndCleanup();
      }, 1500); // Longer delay for removal to complete
      
      // Update variant visibility on product pages after item removal
      if (window.location.pathname.includes('/products/')) {
        setTimeout(() => {
          this.updateVariantOptionsVisibility();
        }, 1600);
      }
    }
  }

  /**
   * Handle membership plan removal and addition clicks
   */
  async handleMembershipRemovalClick(event) {
    // Check if clicked element is a membership removal button
    const membershipRemoveButton = event.target.closest('[data-action="remove-membership"], .membership-plan-toggle[data-action="remove-membership"], .membership-toggle-compact[data-action="remove-membership"]');
    
    if (membershipRemoveButton) {
      event.preventDefault();
      this.log('Manual membership removal detected');
      if (this.membershipReloadTimeout) {
        clearTimeout(this.membershipReloadTimeout);
        this.membershipReloadTimeout = null;
        this.pendingReloadReason = null;
      }
      try {
        sessionStorage.setItem('membership_removal_suppress_readd', String(Date.now() + 60000));
      } catch (e) {}
      this.isConvertingVariants = true;
      this.showMembershipLoadingIndicator('Removing membership...');

      const cartItemKey = membershipRemoveButton.getAttribute('data-cart-item-key');
      if (cartItemKey) {
        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: cartItemKey, quantity: 0 })
          });
          if (response.ok) {
            this.log('Membership line item remove request sent');
            // Don't dispatch cart:refresh here – wait until after conversion to avoid triggering other handlers
          } else {
            const err = await response.json().catch(() => ({}));
            console.warn('Membership removal request failed:', response.status, err);
            this.isConvertingVariants = false;
          }
        } catch (err) {
          console.error('Error removing membership from cart:', err);
          this.isConvertingVariants = false;
        }
      }

      this.waitForMembershipRemovalAndReload();
    }
    
    // Check if clicked element is a membership ADD button (user manually adding back)
    const membershipAddButton = event.target.closest('[data-action="add-membership"], .membership-plan-toggle[data-action="add-membership"]');
    
    if (membershipAddButton) {
      this.log('Manual membership addition detected');

      const skipManagerAdd =
        membershipAddButton.hasAttribute('data-skip-membership-manager') ||
        !!membershipAddButton.closest('.cart-membership-compact, .cart-membership-offer, [data-cart-drawer-membership]');

      if (skipManagerAdd) {
        if (this.DEBUG) console.log('Manual add via local UI - will wait for membership then convert and reload.');
        if (!membershipAddButton.dataset.membershipManagerNotified) {
          membershipAddButton.dataset.membershipManagerNotified = 'true';
          document.dispatchEvent(
            new CustomEvent('membership:manual-add-started', {
              detail: { source: 'cart-drawer', productId: membershipAddButton.dataset.productId }
            })
          );
        }
        this.waitForMembershipThenConvertAndReload();
        return;
      }

      // Respect manual intent by force-adding membership even if auto-add is disabled
      const tier = (membershipAddButton.getAttribute('data-membership-tier') || 'Platinum').trim();
      if (this.membershipReloadTimeout) {
        clearTimeout(this.membershipReloadTimeout);
        this.membershipReloadTimeout = null;
        this.pendingReloadReason = null;
      }
      this.ensureMembershipProductInCart(tier, { force: true });
    }
  }

  /**
   * Wait for membership removal to complete and verify before reloading
   */
  async waitForMembershipRemovalAndReload() {
    const maxAttempts = 10; // Maximum number of checks
    const checkInterval = 500; // Check every 500ms
    let attempts = 0;

    const checkRemoval = async () => {
      attempts++;
      this.log(`Checking if membership removal completed (attempt ${attempts}/${maxAttempts})...`);

      try {
        const cartResponse = await fetch('/cart.js');
        if (!cartResponse.ok) {
          if (attempts < maxAttempts) {
            setTimeout(checkRemoval, checkInterval);
          } else {
            this.log('Could not verify membership removal - reloading anyway');
            this.isConvertingVariants = false;
            this.updateMembershipLoadingMessage('Updating cart...');
            this.showMembershipLoadingIndicator('Updating cart...');
            this.scheduleMembershipStateReload('manual-membership-removal', 500);
          }
          return;
        }

        const cart = await cartResponse.json();
        
        // Check if membership products still exist
        const membershipItems = cart.items.filter(item => {
          const productTitle = (item.product_title || '').toLowerCase();
          return productTitle.includes('plan') || 
                 productTitle.includes('gold') || 
                 productTitle.includes('platinum') ||
                 productTitle.includes('membership');
        });

        if (membershipItems.length === 0) {
          this.log('Membership confirmed removed - waiting 600ms then converting member variants to regular');
          this.updateMembershipLoadingMessage('Converting to regular prices...');
          await new Promise(r => setTimeout(r, 600));
          try {
            await this.convertMemberVariantsToRegular(true);
          } catch (err) {
            console.error('Error converting member variants to regular after removal:', err);
          } finally {
            this.isConvertingVariants = false;
          }
          if (this.membershipReloadTimeout) {
            clearTimeout(this.membershipReloadTimeout);
            this.membershipReloadTimeout = null;
            this.pendingReloadReason = null;
          }
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          this.updateMembershipLoadingMessage('Updating cart...');
          this.showMembershipLoadingIndicator('Updating cart...');
          this.log('Scheduling reload after membership removal (1.5s)');
          this.scheduleMembershipStateReload('manual-membership-removal', 1500);
        } else {
          // Membership still exists, check again
          if (attempts < maxAttempts) {
            this.log(`Membership still in cart, checking again in ${checkInterval}ms...`);
            setTimeout(checkRemoval, checkInterval);
          } else {
            this.log('Membership removal timed out - reloading anyway');
            this.isConvertingVariants = false;
            this.updateMembershipLoadingMessage('Updating cart...');
            this.showMembershipLoadingIndicator('Updating cart...');
            this.scheduleMembershipStateReload('manual-membership-removal', 500);
          }
        }
      } catch (error) {
        console.error('Error checking membership removal:', error);
        if (attempts < maxAttempts) {
          setTimeout(checkRemoval, checkInterval);
        } else {
          this.log('Error verifying removal - reloading anyway');
          this.isConvertingVariants = false;
          this.updateMembershipLoadingMessage('Updating cart...');
          this.showMembershipLoadingIndicator('Updating cart...');
          this.scheduleMembershipStateReload('manual-membership-removal', 500);
        }
      }
    };

    // Start checking after a short delay to let the removal request complete (we may have just sent it above)
    setTimeout(checkRemoval, 400);
  }

  /**
   * When user adds membership via UI (skipManagerAdd): wait for membership to appear in cart,
   * then run our conversion (regular → member), then reload. Ensures first attempt completes fully.
   */
  async waitForMembershipThenConvertAndReload() {
    if (this.manualMembershipAddInProgress) return;
    this.manualMembershipAddInProgress = true;
    this.showMembershipLoadingIndicator('Adding membership...');
    if (this.membershipReloadTimeout) {
      clearTimeout(this.membershipReloadTimeout);
      this.membershipReloadTimeout = null;
      this.pendingReloadReason = null;
    }
    const maxAttempts = 15;
    const checkInterval = 500;
    let attempts = 0;
    const checkMembership = async () => {
      attempts++;
      this.log(`Waiting for membership in cart (attempt ${attempts}/${maxAttempts})...`);
      try {
        const cartRes = await fetch('/cart.js?_' + Date.now());
        if (!cartRes.ok) {
          if (attempts < maxAttempts) setTimeout(checkMembership, checkInterval);
          else {
            this.manualMembershipAddInProgress = false;
            this.scheduleMembershipStateReload('manual-membership-add-ui', 1500);
          }
          return;
        }
        const cart = await cartRes.json();
        const hasMembership = cart.items.some(item => {
          const t = (item.product_title || '').toLowerCase();
          return t.includes('plan') || t.includes('gold') || t.includes('platinum') || t.includes('membership');
        });
        if (hasMembership) {
          this.log('Membership in cart - converting regular variants to member, then reload');
          this.updateMembershipLoadingMessage('Converting to member prices...');
          try {
            await new Promise(r => setTimeout(r, 600));
            await this.convertRegularVariantsToMember(true, true);
          } catch (err) {
            console.error('Error converting after manual add:', err);
          } finally {
            this.manualMembershipAddInProgress = false;
          }
          this.updateMembershipLoadingMessage('Updating cart...');
          this.showMembershipLoadingIndicator('Updating cart...');
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          this.scheduleMembershipStateReload('manual-membership-add-ui', 1500);
          return;
        }
        if (attempts < maxAttempts) {
          setTimeout(checkMembership, checkInterval);
        } else {
          this.manualMembershipAddInProgress = false;
          this.scheduleMembershipStateReload('manual-membership-add-ui', 1500);
        }
      } catch (err) {
        console.error('Error in waitForMembershipThenConvertAndReload:', err);
        if (attempts < maxAttempts) setTimeout(checkMembership, checkInterval);
        else {
          this.manualMembershipAddInProgress = false;
          this.scheduleMembershipStateReload('manual-membership-add-ui', 1500);
        }
      }
    };
    setTimeout(checkMembership, 600);
  }

  /**
   * Detect checkout intent to handle post-login membership cleanup
   */
  handleCheckoutClick(event) {
    try {
      const checkoutBtn = event.target.closest('button[name="checkout"], [type="submit"][name="checkout"], .open-otp-popup');
      if (!checkoutBtn) return;

      // Determine current login status quickly
      const status = this.getCurrentCustomerStatus();
      if (!status.isLoggedIn) {
        // Mark that user intends to checkout after login
        sessionStorage.setItem('pending_checkout_after_login', '1');
        console.log('🧭 Pending checkout after login set');
      }
    } catch (e) {
      console.warn('Failed to process checkout click:', e);
    }
  }

  /**
   * Manual trigger for testing - can be called from console
   */
  async manualCheckForAutoRemoval() {
    console.log('🧪 Manual check for auto-removal triggered');
    await this.checkForEmptyCartAndCleanup();
  }

  /**
   * Check if cart is empty and clean up membership popup cookie
   */
  async checkForEmptyCartAndCleanup() {
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        return;
      }
      
      const cart = await cartResponse.json();
      
      if (this.DEBUG) {
        console.log('📊 Cart items:', cart.items.map(item => ({
          product: item.product_title,
          variant: item.variant_title
        })));
      }
      
      // Check if cart is empty
      if (cart.items.length === 0) {
        return;
      }
      
      // Check if only membership products remain in cart
      // Note: This should ONLY exclude membership PRODUCTS (not member variants of regular products)
      const nonMembershipItems = cart.items.filter(item => {
        const productTitle = (item.product_title || '').toLowerCase();
        // Only checking if it's a membership PRODUCT (like "Gold Plan", "Platinum Plan")
        const isMembershipProduct = productTitle.includes('plan') || 
                                   productTitle.includes('gold') || 
                                   productTitle.includes('platinum') ||
                                   productTitle.includes('membership');
        
        // Return true if it's NOT a membership product (it's a regular product, even if it's a member variant)
        return !isMembershipProduct;
      });
      
      if (this.DEBUG) {
        console.log(`📊 Non-membership products: ${nonMembershipItems.length}`);
        console.log(`📊 Total cart items: ${cart.items.length}`);
      }
      
      // REMOVED: Auto-removal of membership products when only membership remains
      // Client requested to keep membership products in cart even if they're the only items
      
      // Check if we have member variants but no membership plan
      await this.checkForMemberVariantsWithoutMembership(cart);

      // If membership is in cart but some items are non-member variants, convert them to member
      await this.checkForNonMemberVariantsWithMembership(cart);
    } catch (error) {
      console.error('Error checking for empty cart:', error);
    }
  }

  /**
   * After batched add (product + membership in one request): convert any existing non-member
   * variants in cart to member variants, then reload. Call this with the current cart when
   * membership was just added via theme batched add so we don't leave other lines as non-member.
   */
  async convertNonMemberVariantsIfNeededThenReload(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      this.scheduleMembershipStateReload('membership-batched-add', 800);
      return;
    }
    const hasMembership = cart.items.some(item => {
      const productTitle = (item.product_title || '').toLowerCase();
      return productTitle.includes('plan') || productTitle.includes('gold') ||
             productTitle.includes('platinum') || productTitle.includes('membership');
    });
    if (!hasMembership) {
      this.scheduleMembershipStateReload('membership-batched-add', 800);
      return;
    }
    const nonMemberItems = cart.items.filter(item => {
      const productTitle = (item.product_title || '').toLowerCase();
      if (productTitle.includes('plan') || productTitle.includes('gold') || productTitle.includes('platinum') || productTitle.includes('membership')) {
        return false;
      }
      const variantTitle = (item.variant_title || '').toLowerCase();
      return !variantTitle.includes('member') && !variantTitle.includes('beauty pie+');
    });
    if (nonMemberItems.length === 0) {
      this.scheduleMembershipStateReload('membership-batched-add', 800);
      return;
    }
    if (this.isConvertingVariants) {
      this.scheduleMembershipStateReload('membership-batched-add', 800);
      return;
    }
    console.log('Batched add: membership added with product; converting', nonMemberItems.length, 'existing non-member variant(s) to member');
    this.isConvertingVariants = true;
    try {
      await this.convertRegularVariantsToMember(true, true);
      this.scheduleMembershipStateReload('membership-batched-add-converted', 800);
    } catch (error) {
      console.error('Error converting after batched add:', error);
      this.scheduleMembershipStateReload('membership-batched-add', 800);
    } finally {
      this.isConvertingVariants = false;
    }
  }

  /**
   * Check if cart has membership but also non-member variants; if so, convert those to member variants.
   * Ensures "membership in cart" always implies "all product lines are member variants".
   */
  async checkForNonMemberVariantsWithMembership(cart) {
    try {
      if (window.location.pathname === '/cart') return;
      if (this.isEnsuringMembership || this.manualMembershipAddInProgress) {
        this.log('Skipping non-member conversion: membership add in progress');
        return;
      }
      const hasMembership = cart.items.some(item => {
        const productTitle = (item.product_title || '').toLowerCase();
        return productTitle.includes('plan') ||
               productTitle.includes('gold') ||
               productTitle.includes('platinum') ||
               productTitle.includes('membership');
      });
      if (!hasMembership) return;

      const nonMemberItems = cart.items.filter(item => {
        const productTitle = (item.product_title || '').toLowerCase();
        if (productTitle.includes('plan') || productTitle.includes('gold') || productTitle.includes('platinum') || productTitle.includes('membership')) {
          return false;
        }
        const variantTitle = (item.variant_title || '').toLowerCase();
        return !variantTitle.includes('member') && !variantTitle.includes('beauty pie+');
      });

      if (nonMemberItems.length === 0) return;

      if (this.isConvertingVariants) return;

      console.log('Membership in cart but', nonMemberItems.length, 'non-member variant(s) found - converting to member variants');
      if (this.membershipReloadTimeout) {
        clearTimeout(this.membershipReloadTimeout);
        this.membershipReloadTimeout = null;
        this.pendingReloadReason = null;
      }
      this.isConvertingVariants = true;
      try {
        await this.convertRegularVariantsToMember(true, true);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        this.scheduleMembershipStateReload('non-member-variants-converted', 1500);
      } finally {
        this.isConvertingVariants = false;
      }
    } catch (error) {
      console.error('Error in checkForNonMemberVariantsWithMembership:', error);
      this.isConvertingVariants = false;
    }
  }

  /**
   * Check if cart has member variants but no membership plan and add membership if needed
   */
  async checkForMemberVariantsWithoutMembership(cart) {
    try {
      if (this.isConvertingVariants) {
        this.log('Skipping re-add: conversion in progress');
        return;
      }
      try {
        const suppressUntil = sessionStorage.getItem('membership_removal_suppress_readd');
        if (suppressUntil) {
          const until = parseInt(suppressUntil, 10);
          if (Date.now() < until) {
            this.log('Skipping re-add membership: user recently removed membership');
            return;
          }
          sessionStorage.removeItem('membership_removal_suppress_readd');
        }
      } catch (e) {}
      // Find member variants in cart
      const memberVariants = cart.items.filter(item => {
        const variantTitle = (item.variant_title || '').toLowerCase();
        return variantTitle.includes('member') || 
               variantTitle.includes('beauty pie+');
      });
      
      // Find existing membership products in cart
      const existingMembership = cart.items.filter(item => {
        const productTitle = (item.product_title || '').toLowerCase();
        return productTitle.includes('plan') || 
               productTitle.includes('gold') || 
               productTitle.includes('platinum') ||
               productTitle.includes('membership');
      });
      
      if (this.DEBUG) {
        console.log(`📊 Member variants: ${memberVariants.length}, Membership products: ${existingMembership.length}`);
      }
      
      // If we have member variants but no membership plan, add membership
      if (memberVariants.length > 0 && existingMembership.length === 0) {
        // Check if user is an active member first (highest priority)
        const currentStatus = this.getCurrentCustomerStatus();
        if (currentStatus.isLoggedIn && currentStatus.isActiveMember) {
          return;
        }
        
        this.log('Adding membership for member variants');
        await this.ensureMembershipProductInCart('Platinum', { force: true });
      }
    } catch (error) {
      console.error('Error checking for member variants without membership:', error);
    }
  }

  /**
   * Automatically remove membership products and empty the cart
   */
  async autoRemoveMembershipAndEmptyCart(cart) {
    try {
      console.log('🔄 Auto-removing membership products from cart');
      
      // Find all membership products in cart
      const membershipItems = cart.items.filter(item => {
        const productTitle = (item.product_title || '').toLowerCase();
        return productTitle.includes('plan') || 
               productTitle.includes('gold') || 
               productTitle.includes('platinum') ||
               productTitle.includes('membership');
      });
      
      if (membershipItems.length === 0) {
        console.log('No membership items found to remove');
        return;
      }
      
      // Remove each membership item
      for (const membershipItem of membershipItems) {
        try {
          console.log(`Removing membership item: ${membershipItem.title}`);
          
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: membershipItem.key,
              quantity: 0
            })
          });
          
          if (!response.ok) {
            console.error(`Failed to remove membership item ${membershipItem.title}:`, response.status);
          } else {
            console.log(`Successfully removed membership item: ${membershipItem.title}`);
          }
          
          // Small delay between removals to prevent race conditions
          await new Promise(resolve => setTimeout(resolve, 300));
          
        } catch (itemError) {
          console.error(`Error removing membership item ${membershipItem.title}:`, itemError);
        }
      }
      
      // Show user-friendly message
      this.showAutoRemovalMessage();
      
      // Refresh cart display
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
      this.scheduleMembershipStateReload('auto-membership-removal', 1200);
      
    } catch (error) {
      console.error('Error auto-removing membership products:', error);
    }
  }

  /**
   * Show user-friendly message about auto-removal
   */
  showAutoRemovalMessage() {
    // Create a temporary notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #f8f9fa;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      padding: 16px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      max-width: 300px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
    
    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <div style="color: #28a745; font-size: 18px;">✓</div>
        <div>
          <div style="font-weight: 600; margin-bottom: 4px;">Membership Removed</div>
          <div style="font-size: 14px; color: #6c757d;">No products to benefit from membership</div>
        </div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Remove notification after 4 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 4000);
  }

  /**
   * Global function to check for empty cart and clean up cookies
   * This can be called from anywhere in the application
   */
  static async checkForEmptyCartAndCleanupCookies() {
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        console.warn('Failed to fetch cart for empty check:', cartResponse.status);
        return false;
      }
      
      const cart = await cartResponse.json();
      
      // Check if cart is empty
      if (cart.items.length === 0) {
        return true;
      }
      
      // Check if only membership products remain in cart
      const nonMembershipItems = cart.items.filter(item => {
        const productTitle = (item.product_title || '').toLowerCase();
        return !productTitle.includes('plan') && 
               !productTitle.includes('gold') && 
               !productTitle.includes('platinum') &&
               !productTitle.includes('membership');
      });
      
      if (nonMembershipItems.length === 0 && cart.items.length > 0) {
        // REMOVED: Auto-removal of membership products when only membership remains
        // Client requested to keep membership products in cart even if they're the only items
        console.log('✅ Only membership products remain - keeping them in cart as requested');
        return false;
      }
      
      // NEW: Check if we have member variants but no membership plan
      const memberVariants = cart.items.filter(item => {
        const variantTitle = (item.variant_title || '').toLowerCase();
        return variantTitle.includes('member') 
      });
      
      const existingMembership = cart.items.filter(item => {
        const productTitle = (item.product_title || '').toLowerCase();
        return productTitle.includes('plan') || 
               productTitle.includes('gold') || 
               productTitle.includes('platinum') ||
               productTitle.includes('membership');
      });
      
      if (memberVariants.length > 0 && existingMembership.length === 0) {
        console.log('🛒 Member variants found but no membership plan - adding membership');
        
        // Check if user is an active member (don't add if they already have membership benefits)
        // Use the instance method if available, otherwise skip the check
        let isActiveMember = false;
        if (window.membershipCartManager) {
          const currentStatus = window.membershipCartManager.getCurrentCustomerStatus();
          isActiveMember = currentStatus.isLoggedIn && currentStatus.isActiveMember;
        }
        
        if (isActiveMember) {
          console.log('✅ User is active member - no need to add membership plan');
          return false;
        }
        
        // Add membership plan (simplified version for global function)
        try {
          const membershipProductId = window.membershipCartManager?.membershipProducts?.platinum?.productId;
          const membershipVariantId = window.membershipCartManager?.membershipProducts?.platinum?.variantId;
          
          if (membershipProductId && membershipVariantId) {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                items: [{
                  id: membershipVariantId,
                  quantity: 1
                }]
              })
            });
            
            if (response.ok) {
              console.log('✅ Membership plan added for member variants');
              // Don't clear popup cookie here - let it persist for 24 hours
              return true;
            }
          }
        } catch (error) {
          console.error('Error adding membership plan:', error);
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error checking for empty cart:', error);
      return false;
    }
  }

  /**
   * Check if a variant is a member price variant
   */
  isMemberPriceVariant(variant) {
    if (!variant || !variant.title) {
      console.log('Variant check failed: no variant or no title');
      return false;
    }
    
    // Check if the variant title contains "Member Price" or similar
    const memberPriceKeywords = ['member price', 'member', 'membership'];
    const isMemberPrice = memberPriceKeywords.some(keyword => 
      variant.title.toLowerCase().includes(keyword.toLowerCase())
    );
    
    console.log(`Checking if variant "${variant.title}" is member price:`, isMemberPrice);
    
    return isMemberPrice;
  }

  /**
   * Check if a cart item is a member price item
   */
  isMemberPriceCartItem(item) {
    if (!item || !item.variant_title) {
      return false;
    }
    
    const memberPriceKeywords = ['member price', 'member', 'membership'];
    return memberPriceKeywords.some(keyword => 
      item.variant_title.toLowerCase().includes(keyword.toLowerCase())
    );
  }

  /**
   * Ensure the appropriate membership product is in the cart
   */
  async ensureMembershipProductInCart(requiredTier = null, options = {}) {
    const forceAddition = options.force === true;
    const skipConversion = options.skipConversion === true;
    const skipRefresh = options.skipRefresh === true;
    const providedCart = options.cartData;
    
    if (!forceAddition && !this.autoAddMembershipEnabled) {
      console.log('Auto membership addition is disabled - skipping ensureMembershipProductInCart');
      return;
    }
    try {
      // If logged-in active member, do nothing
      if (this.customerStatus.isLoggedIn && this.customerStatus.isActiveMember) {
        console.log('Active member detected - skipping ensureMembershipProductInCart');
        return;
      }
      // Prevent multiple calls in quick succession
      if (this.isEnsuringMembership) {
        console.log('Already ensuring membership product, skipping duplicate call');
        return;
      }
      
      this.isEnsuringMembership = true;
      
      // Use provided cart data if available, otherwise fetch it
      let cart;
      if (providedCart) {
        cart = providedCart;
        console.log('Using provided cart data to avoid duplicate fetch');
      } else {
        const cartResponse = await fetch('/cart.js');
        if (!cartResponse.ok) {
          this.isEnsuringMembership = false;
          throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
        }
        cart = await cartResponse.json();
      }
      
      console.log('Analyzing cart for membership requirements:', cart.items);
      
      // Quick check: If ANY membership product exists in cart, skip everything
      const anyMembershipExists = cart.items.some(item => 
        item.product_id === this.membershipProducts.gold.productId ||
        item.product_id === this.membershipProducts.platinum.productId
      );
      
      if (anyMembershipExists) {
        console.log('Membership already in cart - skipping all membership logic');
        this.isEnsuringMembership = false;
        return; // Exit immediately - no loading, no popup, nothing ✅
      }
      
      // Find all member price items in the cart
      const memberPriceItems = cart.items.filter(item => 
        this.isMemberPriceCartItem(item)
      );
      
      if (memberPriceItems.length === 0 && !requiredTier) {
        console.log('No member price items in cart and no required tier, no membership needed');
        this.isEnsuringMembership = false;
        return;
      }
      
      // Determine the tier to add
      let tierToAdd = requiredTier;
      if (!tierToAdd) {
        // Get tier from current product if available
        const currentProductHandle = this.getCurrentProductHandle();
        if (currentProductHandle) {
          tierToAdd = this.getProductMembershipTier(currentProductHandle);
        }
      }
      
      if (!tierToAdd) {
        tierToAdd = 'Platinum'; // Default to Platinum
      }
      
      console.log('Tier to add:', tierToAdd);
      
      // NOW show loading indicator (all checks passed, we will add membership)
      console.log('Adding membership to cart - showing loading');
      this.showMembershipLoadingIndicator('Adding membership plan...');
      
      // Remove any existing membership products (we only want one)
      const existingMemberships = cart.items.filter(item => 
        item.product_id === this.membershipProducts.gold.productId ||
        item.product_id === this.membershipProducts.platinum.productId
      );
      
      if (existingMemberships.length > 0) {
        console.log('Removing existing membership products');
        for (const membership of existingMemberships) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: membership.key,
              quantity: 0
            })
          });
        }
      }
      
      // Add the required membership product
      let membershipKey = (tierToAdd || 'Platinum').toLowerCase().trim();
      if (!['gold', 'platinum'].includes(membershipKey)) {
        console.warn(`Unknown membership tier "${tierToAdd}" - defaulting to Platinum`);
        membershipKey = 'platinum';
      }

      let membershipProduct = this.membershipProducts[membershipKey];

      if (!membershipProduct || !membershipProduct.variantId) {
        console.warn('Membership product config missing, retrying initialization');
        this.initializeMembershipProducts();
        membershipProduct = this.membershipProducts[membershipKey];
      }

      if (!membershipProduct || !membershipProduct.variantId) {
        console.error(`Cannot add membership tier "${membershipKey}" - variant ID missing.`);
        this.hideMembershipLoadingIndicator();
        this.isEnsuringMembership = false;
        return;
      }

      console.log('Adding membership product:', membershipProduct);
      
      // Update loading message
      this.updateMembershipLoadingMessage('Adding membership plan...');
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: membershipProduct.variantId,
            quantity: 1
          }]
        })
      });
      
      if (response.ok) {
        console.log(`${tierToAdd} membership product added to cart`);
        
        // Only refresh cart sections if not skipped (for member variant adds, we skip this)
        if (!skipRefresh) {
          await this.refreshCartSections();
        }
        
        // Only convert regular variants if not skipped (for member variant adds, skip conversion)
        if (!skipConversion) {
          if (this.membershipReloadTimeout) {
            clearTimeout(this.membershipReloadTimeout);
            this.membershipReloadTimeout = null;
            this.pendingReloadReason = null;
          }
          this.updateMembershipLoadingMessage('Converting to member prices...');
          console.log('Converting regular variants to member variants after membership addition');
          await this.convertRegularVariantsToMember(true, true);
        } else {
          console.log('Skipping variant conversion - member variants already in cart');
        }
        
        // Hide loading indicator immediately for faster UX
        this.hideMembershipLoadingIndicator();
        
        // Show popup immediately (no delay for member variant adds)
        if (skipConversion) {
          this.showMembershipPopup();
          this.scheduleMembershipStateReload('membership-added', 300);
        } else {
          setTimeout(() => {
            this.showMembershipPopup();
          }, 100);
          this.scheduleMembershipStateReload('membership-added', 1500);
        }
        
      } else {
        throw new Error('Failed to add membership product');
      }
      
    } catch (error) {
      console.error('Error ensuring membership product in cart:', error);
      // Hide loading indicator on error
      this.hideMembershipLoadingIndicator();
    } finally {
      this.isEnsuringMembership = false;
    }
  }



  /**
   * Show membership loading indicator
   */
  showMembershipLoadingIndicator(message = 'Processing...') {
    // Use theme's loading system
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    
    // Also show custom membership loading overlay
    let overlay = document.querySelector('.membership-loading-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'membership-loading-overlay';
      overlay.innerHTML = `
        <div class="membership-loading-content">
          <div class="membership-loading-spinner"></div>
          <p class="membership-loading-text">${message}</p>
        </div>
      `;
      document.body.appendChild(overlay);
    } else {
      overlay.querySelector('.membership-loading-text').textContent = message;
      overlay.style.display = 'flex';
    }
    
    console.log('🔄 Showing membership loading indicator:', message);
  }

  /**
   * Update membership loading message
   */
  updateMembershipLoadingMessage(message) {
    const overlay = document.querySelector('.membership-loading-overlay');
    if (overlay) {
      const textElement = overlay.querySelector('.membership-loading-text');
      if (textElement) {
        textElement.textContent = message;
        console.log('🔄 Updated loading message:', message);
      }
    }
  }

  /**
   * Hide membership loading indicator
   */
  hideMembershipLoadingIndicator() {
    // Use theme's loading system
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    
    // Hide custom overlay
    const overlay = document.querySelector('.membership-loading-overlay');
    if (overlay) {
      overlay.style.display = 'none';
    }
    
    console.log('✅ Hidden membership loading indicator');
  }

  /**
   * Reload the page after membership state changes so product pricing syncs
   */
  scheduleMembershipStateReload(reason = 'membership-change', delay = 1200) {
    if (this.suppressNextCartReload) {
      this.suppressNextCartReload = false;
      return;
    }

    if (this.membershipReloadTimeout) {
      if (this.DEBUG) {
        console.log(
          `⏳ Membership reload already scheduled for "${this.pendingReloadReason}" - skipping duplicate (${reason})`
        );
      }
      return;
    }

    const sanitizedDelay = typeof delay === 'number' && delay >= 0 ? delay : 1200;
    this.pendingReloadReason = reason;

    this.membershipReloadTimeout = setTimeout(() => {
      console.log(`🔄 Reloading page after ${reason}`);
      // Set flag to reopen cart drawer after reload
      try {
        sessionStorage.setItem('membership_reload_open_cart', 'true');
        console.log('✅ Set flag to reopen cart drawer after reload');
      } catch (e) {
        console.warn('Could not set sessionStorage flag:', e);
      }
      this.membershipReloadTimeout = null;
      this.pendingReloadReason = null;
      window.location.reload();
    }, sanitizedDelay);
  }

  /**
   * Refresh cart sections without page reload
   */
  async refreshCartSections() {
    try {
      console.log('🔄 Refreshing cart sections...');
      
      // Get fresh cart data with sections
      const sectionsToBundle = ['cart-drawer', 'main-cart'];
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
      }
      
      const cart = await cartResponse.json();
      
      // Fetch updated sections using Shopify's section rendering
      const sectionsToUpdate = ['main-cart', 'cart-drawer'];
      const sectionsResponse = await fetch(`/cart?${sectionsToUpdate.map(section => `sections=${section}`).join('&')}`, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      // Also try fetching the full cart page as a fallback
      let cartPageResponse = null;
      if (!sectionsResponse.ok) {
        console.log('Sections fetch failed, trying full cart page...');
        cartPageResponse = await fetch('/cart', {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
      }
      
      if (sectionsResponse.ok || cartPageResponse?.ok) {
        const responseToUse = sectionsResponse.ok ? sectionsResponse : cartPageResponse;
        const sectionsHtml = await responseToUse.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionsHtml, 'text/html');
        
        // Update cart membership offer section
        const membershipOffer = doc.querySelector('.cart-membership-offer');
        if (membershipOffer) {
          const existingOffer = document.querySelector('.cart-membership-offer');
          if (existingOffer) {
            existingOffer.outerHTML = membershipOffer.outerHTML;
            console.log('✅ Updated membership offer section');
          }
        }
        
        // Update order summary section
        const orderSummary = doc.querySelector('.order-summary__body');
        if (orderSummary) {
          const existingSummary = document.querySelector('.order-summary__body');
          if (existingSummary) {
            existingSummary.innerHTML = orderSummary.innerHTML;
            console.log('✅ Updated order summary section');
          }
        }
        
        // Update cart items if present
        const cartItems = doc.querySelector('.cart-items, .cart__items, [data-cart-items]');
        if (cartItems) {
          const existingItems = document.querySelector('.cart-items, .cart__items, [data-cart-items]');
          if (existingItems) {
            existingItems.innerHTML = cartItems.innerHTML;
            console.log('✅ Updated cart items section');
          }
        }
        
        // Update cart right section (contains totals)
        const cartRight = doc.querySelector('.cart__right');
        if (cartRight) {
          const existingCartRight = document.querySelector('.cart__right');
          if (existingCartRight) {
            existingCartRight.innerHTML = cartRight.innerHTML;
            console.log('✅ Updated cart right section (totals)');
          }
        }
        
        // Update specific totals elements within cart right
        const totalsSelectors = [
          '.cart__right .v-stack', // The main totals container
          '.cart__right .h-stack', // Individual total rows
          '.cart__right span.medium', // Total amount
          '[data-cart-total]',
          '.order-summary tr:last-child td:last-child',
          '.cart-total',
          '.total-price',
          '.cart__total'
        ];
        
        for (const selector of totalsSelectors) {
          const totalsElement = doc.querySelector(selector);
          if (totalsElement) {
            const existingElement = document.querySelector(selector);
            if (existingElement) {
              existingElement.innerHTML = totalsElement.innerHTML;
              console.log(`✅ Updated totals using selector: ${selector}`);
              break;
            }
          }
        }
        
        // Update cart count if present
        const cartCount = doc.querySelector('.cart-count');
        if (cartCount) {
          const existingCount = document.querySelector('.cart-count');
          if (existingCount) {
            existingCount.innerHTML = cartCount.innerHTML;
            console.log('✅ Updated cart count');
          }
        }
      }
      
      // Dispatch cart change event to update UI
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'membership:add',
          cart: cart
        }
      }));
      
      // Also dispatch cart refresh event
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cart }
      }));
      
      // Force a cart drawer refresh if it exists
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.refreshCart === 'function') {
        cartDrawer.refreshCart();
        console.log('✅ Triggered cart drawer refresh');
      }
      
      // Force cart count update
      const cartCountElements = document.querySelectorAll('cart-count');
      cartCountElements.forEach(element => {
        if (typeof element.updateFromServer === 'function') {
          element.updateFromServer();
        }
      });
      
      // Force update cart totals by dispatching a more specific event
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cart }
      }));
      
      // Manually update membership toggle button state
      await this.updateMembershipToggleButton();
      
      // If cart right section wasn't updated, try to manually update totals
      await this.manuallyUpdateCartTotals(cart);
      
      console.log('✅ Cart sections refreshed successfully');
      
    } catch (error) {
      console.error('Error refreshing cart sections:', error);
      // Fallback to page reload if section refresh fails
      if (window.location.pathname === '/cart') {
        console.log('Falling back to page reload');
        // Set flag to reopen cart drawer after reload
        try {
          sessionStorage.setItem('membership_reload_open_cart', 'true');
          console.log('✅ Set flag to reopen cart drawer after reload');
        } catch (e) {
          console.warn('Could not set sessionStorage flag:', e);
        }
        window.location.reload();
      }
    }
  }

  /**
   * Manually update cart totals if section refresh doesn't work
   */
  async manuallyUpdateCartTotals(cart) {
    try {
      console.log('🔄 Manually updating cart totals...');
      
      // Find the total price element in cart right
      const totalElement = document.querySelector('.cart__right .h-stack:last-child span.medium:last-child');
      if (totalElement) {
        // Format the total price
        const totalPrice = cart.total_price;
        const formattedPrice = new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR'
        }).format(totalPrice / 100);
        
        totalElement.textContent = formattedPrice;
        console.log('✅ Manually updated cart total:', formattedPrice);
      }
      
      // Update membership fee if present
      // Note: :contains() is not valid in querySelector, so we iterate
      const hStacks = document.querySelectorAll('.cart__right .h-stack');
      for (const stack of hStacks) {
        const spans = stack.querySelectorAll('span');
        for (let i = 0; i < spans.length - 1; i++) {
          if (spans[i].textContent.includes('Membership fee')) {
            const membershipFeeElement = spans[i + 1];
            if (membershipFeeElement) {
              const membershipItem = cart.items.find(item => 
                item.product_title && (
                  item.product_title.toLowerCase().includes('plan') ||
                  item.product_title.toLowerCase().includes('gold') ||
                  item.product_title.toLowerCase().includes('platinum')
                )
              );
              
              if (membershipItem && this.DEBUG) {
                const membershipFee = new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR'
                }).format(membershipItem.final_line_price / 100);
                
                membershipFeeElement.textContent = membershipFee;
              }
            }
            break;
          }
        }
      }
      
    } catch (error) {
      console.error('Error manually updating cart totals:', error);
    }
  }

  /**
   * Check if cart is empty by looking at cart drawer header
   * Liquid renders: "Cart" when empty, "Cart (2)" when not empty
   */
  isCartEmpty() {
    try {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        return false; // Can't determine, assume not empty to be safe
      }
      
      // Check cart drawer header - Liquid renders item count only when cart is not empty
      const cartHeader = cartDrawer.querySelector('h2');
      if (cartHeader) {
        const headerText = cartHeader.textContent.trim();
        // If header doesn't have item count in parentheses, cart is empty
        // Liquid renders: "Cart" when empty, "Cart (2)" when not empty
        if (!headerText.match(/\(\d+\)/)) {
          return true;
        }
      }
      
      // Also check for empty-state element
      const emptyState = cartDrawer.querySelector('.empty-state');
      if (emptyState) {
        return true;
      }
      
      return false;
    } catch (e) {
      console.warn('Error checking cart empty state:', e);
      return false; // Assume not empty to be safe
    }
  }

  /**
   * Check if we need to reopen cart drawer after reload and open it
   */
  checkAndOpenCartDrawerAfterReload() {
    try {
      const shouldOpenCart = sessionStorage.getItem('membership_reload_open_cart');
      if (shouldOpenCart !== 'true') {
        return; // No flag set, exit early
      }
      
      console.log('🛒 Detected flag to reopen cart drawer after reload');
      // Clear the flag immediately
      sessionStorage.removeItem('membership_reload_open_cart');
      
      // Wait for DOM to be ready, then check and open
      const openCartDrawer = () => {
        const cartDrawer = document.querySelector('cart-drawer');
        
        if (!cartDrawer) {
          console.warn('⚠️ Cart drawer not found');
          return;
        }
        
        // Check if cart is empty - don't open if empty
        if (this.isCartEmpty()) {
          console.log('🛒 Cart is empty - skipping cart drawer open');
          return;
        }
        
        // Try to open the cart drawer
        try {
          if (typeof cartDrawer.show === 'function') {
            cartDrawer.show();
            console.log('✅ Opened cart drawer after reload');
          } else {
            // Fallback: try clicking cart icon
            const cartIcon = document.querySelector('[data-cart-icon], .cart-icon, [aria-label*="cart" i], button[aria-label*="cart" i]');
            if (cartIcon) {
              cartIcon.click();
              console.log('✅ Opened cart drawer via cart icon click');
            } else {
              console.warn('⚠️ Could not find cart drawer show method or cart icon');
            }
          }
        } catch (e) {
          console.error('Error opening cart drawer:', e);
        }
      };
      
      // Try immediately, then retry once after a short delay if needed
      if (document.readyState === 'complete' || document.readyState === 'interactive') {
        setTimeout(openCartDrawer, 500);
      } else {
        window.addEventListener('load', () => {
          setTimeout(openCartDrawer, 500);
        });
      }
    } catch (e) {
      console.warn('Error checking cart drawer reopen flag:', e);
    }
  }

  /**
   * Update membership toggle button state
   */
  async updateMembershipToggleButton() {
    try {
      console.log('🔄 Updating membership toggle button state...');
      
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      const membershipToggle = document.querySelector('.membership-plan-toggle');
      if (!membershipToggle) {
        console.log('No membership toggle button found');
        return;
      }
      
      const productId = membershipToggle.getAttribute('data-product-id');
      if (!productId) {
        console.log('No product ID found on membership toggle button');
        return;
      }
      
      // Check if membership product is in cart
      const existingMembership = cart.items.find(item => 
        item.product_id && item.product_id.toString() === productId.toString()
      );
      
      if (existingMembership) {
        // Update button to "remove" state
        membershipToggle.classList.add('is-active');
        membershipToggle.setAttribute('data-action', 'remove-membership');
        membershipToggle.setAttribute('data-cart-item-key', existingMembership.key);
        membershipToggle.querySelector('.membership-plan-toggle__text').textContent = 'Remove Plan';
        console.log('✅ Updated membership toggle to remove state');
      } else {
        // Update button to "add" state
        membershipToggle.classList.remove('is-active');
        membershipToggle.setAttribute('data-action', 'add-membership');
        membershipToggle.removeAttribute('data-cart-item-key');
        membershipToggle.querySelector('.membership-plan-toggle__text').textContent = 'Add Plan';
        console.log('✅ Updated membership toggle to add state');
      }
      
    } catch (error) {
      console.error('Error updating membership toggle button:', error);
    }
  }

  /**
   * Get the current product handle
   */
  getCurrentProductHandle() {
    // Try multiple methods to get the product handle
    
    // Method 1: From URL
    const urlPath = window.location.pathname;
    const productMatch = urlPath.match(/\/products\/([^\/]+)/);
    if (productMatch) {
      return productMatch[1];
    }
    
    // Method 2: From script data
    const membershipTierScript = document.querySelector('script[data-membership-tier]');
    if (membershipTierScript) {
      try {
        const data = JSON.parse(membershipTierScript.textContent);
        if (data.product_handle) {
          return data.product_handle;
        }
      } catch (e) {
        console.error('Error parsing membership tier data:', e);
      }
    }
    
    // Method 3: From variant picker
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      const handle = variantPicker.getAttribute('handle');
      if (handle) {
      return handle;
      }
    }

    // Method 4: From product form
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const handleInput = productForm.querySelector('input[name="handle"]');
      if (handleInput) {
        return handleInput.value;
      }
    }
    
    console.log('Could not determine current product handle');
    return null;
  }

  /**
   * Get the membership tier for a product
   */
    getProductMembershipTier(productHandle) {
    if (!productHandle) {
      console.log('No product handle provided');
      return null;
    }
    
    // Try to get tier from DOM data
    const membershipTierScript = document.querySelector('script[data-membership-tier]');
    if (membershipTierScript) {
      try {
        const data = JSON.parse(membershipTierScript.textContent);
        if (data.tiers && Array.isArray(data.tiers)) {
          // Return the highest tier
          if (data.tiers.includes('Platinum')) {
            return 'Platinum';
          } else if (data.tiers.includes('Gold')) {
            return 'Gold';
          }
        }
      } catch (e) {
        console.error('Error parsing membership tier data:', e);
      }
    }
    
    // Fallback: check if current product handle matches
    const currentHandle = this.getCurrentProductHandle();
    if (currentHandle === productHandle) {
      // Use the same logic as above for current product
      const membershipTierScript = document.querySelector('script[data-membership-tier]');
      if (membershipTierScript) {
        try {
          const data = JSON.parse(membershipTierScript.textContent);
          if (data.tiers && Array.isArray(data.tiers)) {
            if (data.tiers.includes('Platinum')) {
              return 'Platinum';
            } else if (data.tiers.includes('Gold')) {
              return 'Gold';
            }
          }
        } catch (e) {
          console.error('Error parsing membership tier data:', e);
        }
      }
    }
    
    console.log('No valid membership tier found for product:', productHandle);
      return null;
  }

  /**
   * Handle visibility change to pause/resume monitoring
   */
  handleVisibilityChange() {
    if (document.hidden) {
      console.log('Page hidden, pausing cart monitoring');
    } else {
      console.log('Page visible, resuming cart monitoring');
    }
  }

  /**
   * Setup login detection to handle active members with membership plans in cart
   */
  setupLoginDetection() {
    console.log(window.location.href,'Setting up login detection for membership plan management');
            this.checkLoginStatusAndUpdateCart();
    // Method 1: Listen for URL changes (common login pattern)
    let currentUrl = window.location.href;
    const checkUrlChange = () => {
      if (window.location.href !== currentUrl) {
        currentUrl = window.location.href;
        console.log('URL changed, checking for login status');
        this.checkLoginStatusAndUpdateCart();
      }
    };
    
    // Check URL changes periodically
    setInterval(checkUrlChange, 1000);
    
    // Method 2: Listen for DOM changes that indicate login
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check for login indicators
              if (node.classList && (
                node.classList.contains('customer-logged-in') ||
                node.classList.contains('is-logged-in') ||
                node.id === 'customer-account' ||
                node.querySelector && node.querySelector('[data-customer-logged-in]')
              )) {
                console.log('Login indicator detected in DOM, checking status');
                this.checkLoginStatusAndUpdateCart();
              }
            }
          }
        }
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
  }

  /**
   * Setup OTP integration for immediate login detection and cart cleanup
   */
  setupOtpIntegration() {
    console.log('Setting up OTP integration for membership cart');
    
    // Use polling to detect when user reaches checkout page with pending flag
    let lastUrl = window.location.href;
    setInterval(() => {
      const currentUrl = window.location.href;
      const pendingCheckout = sessionStorage.getItem('pending_checkout_after_login') === '1';
      
      // Check if we just navigated to checkout page with pending flag
      if (currentUrl !== lastUrl && currentUrl.includes('/checkout') && pendingCheckout) {
        console.log('🔄 Detected navigation to checkout with pending flag - running cart cleanup');
        sessionStorage.removeItem('pending_checkout_after_login');
        this.checkLoginStatusAndUpdateCart().then(() => {
          console.log('✅ Cart cleanup completed on checkout page');
        }).catch((error) => {
          console.error('❌ Cart cleanup failed on checkout page:', error);
        });
      }
      
      lastUrl = currentUrl;
    }, 500); // Check every 500ms for faster detection
    
    // Also listen for beforeunload to catch any last-minute redirects
    window.addEventListener('beforeunload', () => {
      const pendingCheckout = sessionStorage.getItem('pending_checkout_after_login') === '1';
      if (pendingCheckout) {
        console.log('🔄 Page unloading with pending checkout - attempting quick cleanup');
        // Use synchronous approach for beforeunload
        this.quickCartCleanup();
      }
    });
  }

  /**
   * Quick synchronous cart cleanup for beforeunload event
   */
  quickCartCleanup() {
    try {
      // This is a simplified version that doesn't rely on async operations
      const currentStatus = this.getCurrentCustomerStatus();
      if (currentStatus.isLoggedIn && currentStatus.isActiveMember) {
        console.log('🧹 Quick cleanup: User is active member, removing membership plan');
        // Use fetch with async: false for synchronous operation
        const xhr = new XMLHttpRequest();
        xhr.open('GET', '/cart.js', false);
        xhr.send();
        
        if (xhr.status === 200) {
          const cart = JSON.parse(xhr.responseText);
          const membershipItems = cart.items.filter(item => 
            this.isMemberPriceCartItem(item)
          );
          
          if (membershipItems.length > 0) {
            console.log('🧹 Quick cleanup: Found membership items to remove');
            // Note: We can't do async operations in beforeunload, so we'll rely on the polling method
          }
        }
      }
    } catch (error) {
      console.warn('Quick cleanup failed:', error);
    }
  }

  /**
   * Check if OTP customer is an active member
   */
  /*
  async checkOtpCustomerStatus() {
    if (!window.simplyOtp?.customer) {
      return { isLoggedIn: false, isActiveMember: false, customerTags: [] };
    }
    
    try {
      console.log('Checking OTP customer status...');
      const response = await fetch('/account');
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Look for customer tags in the HTML
        const customerTagsMeta = doc.querySelector('meta[name="customer-tags"]');
        if (customerTagsMeta) {
          const customerTags = customerTagsMeta.content.split(',').map(tag => tag.trim());
          const isActiveMember = customerTags.includes('is_active_member');
          console.log('OTP customer tags found:', customerTags);
          return { isLoggedIn: true, isActiveMember, customerTags };
        }
      }
    } catch (error) {
      console.warn('Could not fetch customer data for OTP user:', error);
    }
    
    return { isLoggedIn: true, isActiveMember: false, customerTags: [] };
  }
*/
  /**
   * Check login status and update cart if needed for active members
   */
  async checkLoginStatusAndUpdateCart() {
    try {
     // console.log('Checking login status and updating cart for active members');
      // Get current customer status
      let currentStatus = this.getCurrentCustomerStatus();
     // console.log('Current customer status:', currentStatus);
      
      // If OTP customer detected, get more detailed status
     /* if (window.simplyOtp?.customer && currentStatus.isLoggedIn) {
        console.log('OTP customer detected, fetching detailed status...');
        const otpStatus = await this.checkOtpCustomerStatus();
        currentStatus = { ...currentStatus, ...otpStatus };
        console.log('Updated customer status with OTP data:', currentStatus);
      }
      
      // Check if status changed from not logged in to logged in active member
      const wasLoggedIn = this.customerStatus.isLoggedIn;
      const wasActiveMember = this.customerStatus.isActiveMember;
      */

      // Update stored status
      //this.customerStatus = currentStatus;
      
      const pendingCheckout = sessionStorage.getItem('pending_checkout_after_login') === '1';
      //console.log(currentStatus,"OPENDNDIDIDI",pendingCheckout)
      // If user just logged in and is now an active member
      if (currentStatus.isLoggedIn && currentStatus.isActiveMember) {
        this.log('User just logged in as active member - checking cart for membership plan and converting variants');
        await this.removeMembershipPlanForActiveMember(false);
        
        // Convert all regular variants to member variants
        console.log('Converting regular variants to member variants for active member');
        await this.convertRegularVariantsToMember();
        
        if (pendingCheckout) {
          sessionStorage.removeItem('pending_checkout_after_login');
          console.log('➡️ Redirecting to checkout after cleanup');
          window.location.assign('/checkout');
        }
      }
      // If user was logged in but not active member, now is active member
      // else if (currentStatus.isLoggedIn && currentStatus.isActiveMember) {
      //   console.log('User status changed to active member - checking cart for membership plan');
      //   await this.removeMembershipPlanForActiveMember();
      //   if (pendingCheckout) {
      //     sessionStorage.removeItem('pending_checkout_after_login');
      //     console.log('➡️ Redirecting to checkout after cleanup');
      //     window.location.assign('/checkout');
      //   }
      // }
      // If user logged in but not active member, clear pending flag to avoid stale redirects
      else if (currentStatus.isLoggedIn && !currentStatus.isActiveMember) {
        if (pendingCheckout) {
          sessionStorage.removeItem('pending_checkout_after_login');
          console.log('Clearing pending checkout flag - user is not an active member');
           window.location.assign('/checkout');
        }
      }
      
    } catch (error) {
      console.error('Error checking login status and updating cart:', error);
    }
  }

  /**
   * Get current customer status from various sources
   */
  getCurrentCustomerStatus() {
    const status = {
      isLoggedIn: false,
      isActiveMember: false,
      customerTags: []
    };
    
    try {
      // Method 1: Check window.customerInfo (static from page load)
      if (window.customerInfo) {
        status.isLoggedIn = !!window.customerInfo.isLoggedIn;
        status.isActiveMember = !!window.customerInfo.isActiveMember;
        status.customerTags = Array.isArray(window.customerInfo.customerTags) ? window.customerInfo.customerTags : [];
      }
      // Method 2: Check window.themeSettings.customer (static from page load)
      else if (window.themeSettings && window.themeSettings.customer) {
        const c = window.themeSettings.customer;
        status.isLoggedIn = !!c.isLoggedIn;
        status.isActiveMember = !!c.isActiveMember;
        status.customerTags = Array.isArray(c.customerTags) ? c.customerTags : [];
      }
      
      // Method 3: Check OTP customer status (dynamic)
      if (window.simplyOtp?.customer) {
        status.isLoggedIn = true;
        console.log('OTP customer detected - will check active member status separately');
        
        // For OTP customers, we'll check active member status in a separate async call
        // This is handled in checkLoginStatusAndUpdateCart
      }
      
      // Method 4: Check DOM indicators (dynamic)
      const customerAccount = document.querySelector('[data-customer-logged-in], .customer-logged-in, #customer-account');
      if (customerAccount) {
        status.isLoggedIn = true;
      }
      
      // Check for active member indicators
      const activeMemberIndicator = document.querySelector('.active-member-notice, [data-active-member]');
      if (activeMemberIndicator) {
        status.isActiveMember = true;
        
      }
      
      console.log('Retrieved customer status:', status);
      return status;
      
    } catch (error) {
      console.error('Error getting customer status:', error);
      return status;
    }
  }

  /**
   * Remove membership plan from cart for active members
   */
  async removeMembershipPlanForActiveMember(redirect='true') {
    try {
      console.log('Removing membership plan for active member');
      
      // Get current cart
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
      }
      
      const cart = await cartResponse.json();
      console.log('Current cart items:', cart.items);
      
      // Find membership products in cart
      const membershipItems = cart.items.filter(item => 
        item.product_title && (
          item.product_title.toLowerCase().includes('plan') ||
          item.product_title.toLowerCase().includes('gold') ||
          item.product_title.toLowerCase().includes('platinum') ||
          item.product_title.toLowerCase().includes('membership')
        )
      );
      
      if (membershipItems.length === 0) {
        console.log('No membership products found in cart');
        return;
      }
      
      console.log('Found membership products in cart:', membershipItems);
      
      // Remove all membership products
      for (const membershipItem of membershipItems) {
        console.log(`Removing membership product: ${membershipItem.product_title}`);
        
        const removeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: membershipItem.key,
            quantity: 0
          })
        });
        
        if (!removeResponse.ok) {
          console.error('Failed to remove membership product:', removeResponse.status);
          continue;
        }
        
        console.log(`Successfully removed ${membershipItem.product_title}`);
      }
      
      // Show success message to user
      this.showActiveMemberMessage();
      
      // Dispatch cart refresh event
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
      if (!redirect) {
        this.scheduleMembershipStateReload('active-member-membership-removal', 1000);
      }
      
    } catch (error) {
      console.error('Error removing membership plan for active member:', error);
    }
  }

  /**
   * Show message to active member that membership plan was removed
   */
  showActiveMemberMessage() {
    console.log('Showing active member message');
    
    // Create success message
    const message = document.createElement('div');
    message.className = 'active-member-message';
    message.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #10b981;
      color: white;
      padding: 12px 16px;
      border-radius: 8px;
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      max-width: 300px;
    `;
    message.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="font-size: 16px;">✓</span>
        <span>Membership plan removed - you're already an active member!</span>
      </div>
    `;
    
    // Add to page
    document.body.appendChild(message);
    
    // Remove after 5 seconds
    setTimeout(() => {
      if (message.parentNode) {
        message.parentNode.removeChild(message);
      }
    }, 5000);
  }

  /**
   * Set up DOM monitoring for cart changes as a fallback detection method
   */
  setupCartDOMMonitoring() {
    try {
      // Look for cart containers
      const cartSelectors = [
        '.cart',
        '.cart__left',
        '.order-summary__body',
        '.cart-items',
        '[data-cart-items]',
        '.cart-form'
      ];
      
      let cartContainer = null;
      for (const selector of cartSelectors) {
        cartContainer = document.querySelector(selector);
        if (cartContainer) {
          //console.log(`Found cart container: ${selector}`);
          break;
        }
      }
      
      if (!cartContainer) {
       // console.log('No cart container found for DOM monitoring');
        return;
      }
      
      // Set up MutationObserver to watch for changes in cart items
      this.cartObserver = new MutationObserver((mutations) => {
        let shouldCheck = false;
        
        mutations.forEach((mutation) => {
          // Check if items were removed or added
          if (mutation.type === 'childList') {
            if (mutation.removedNodes.length > 0 || mutation.addedNodes.length > 0) {
              shouldCheck = true;
            }
          }
        });
        
        if (shouldCheck) {
          //console.log('🔄 Cart DOM changes detected - checking for auto-removal');
          // Debounce the check to prevent excessive calls
          clearTimeout(this.cartCheckTimeout);
          this.cartCheckTimeout = setTimeout(() => {
            this.checkForEmptyCartAndCleanup();
          }, 1000);
        }
      });
      
      // Start observing
      this.cartObserver.observe(cartContainer, {
        childList: true,
        subtree: true
      });
      
      //console.log('Cart DOM monitoring set up successfully');
      
    } catch (error) {
      console.error('Error setting up cart DOM monitoring:', error);
    }
  }

  /**
   * Set up periodic monitoring for auto-removal (more aggressive detection)
   */
  setupPeriodicAutoRemovalCheck() {
    if (this.DEBUG) console.log('🔄 Setting up periodic auto-removal check (every 10 seconds)');
    
    this.autoRemovalInterval = setInterval(async () => {
      if (!document.hidden && !this.isProcessing) {
        try {
          await this.checkForEmptyCartAndCleanup();
        } catch (error) {
          console.error('Error in periodic auto-removal check:', error);
        }
      }
    }, 10000); // Check every 10 seconds (reduced from 3)
  }

  /**
   * Set up periodic cart monitoring
   */
  setupCartMonitoring() {
    // COMMENTED OUT: Periodic cart monitoring not needed
    // This was causing unnecessary API calls and potential issues
    console.log('Periodic cart monitoring disabled');
    
    /*
    setInterval(async () => {
      if (!document.hidden && !this.isProcessing) {
        await this.checkAndMaintainMembershipProducts();
      }
    }, this.cartCheckInterval);
    */
  }

  /**
   * Check and maintain membership products in cart
   */
  async checkAndMaintainMembershipProducts() {
    // COMMENTED OUT: This function was causing unnecessary cart operations
    // Only needed for specific scenarios, not for general maintenance
  //  console.log('Cart maintenance check skipped - not needed for current implementation');
    
    /*
    try {
      this.isProcessing = true;
      
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
      }
      
      const cart = await cartResponse.json();
      
      // Check if we have member price items but no membership
      const memberPriceItems = cart.items.filter(item => 
        this.isMemberPriceCartItem(item)
      );
      
      const membershipItems = cart.items.filter(item => 
        item.product_id === this.membershipProducts.gold.productId ||
        item.product_id === this.membershipProducts.platinum.productId
      );
      
      if (memberPriceItems.length > 0 && membershipItems.length === 0) {
        console.log('Found member price items but no membership, adding membership');
        // Force add membership for member price items (bypass suppression)
        await this.ensureMembershipProductInCart('Platinum', { force: true });
      }
      
    } catch (error) {
      console.error('Error checking membership products:', error);
    } finally {
      this.isProcessing = false;
    }
    */
  }

  /**
   * Check cart page for popup display
   */
  async checkCartPageForPopup() {
    console.log('🔍 Checking cart page for popup display');
    console.log('📍 Current URL:', window.location.href);
    console.log('📍 Current pathname:', window.location.pathname);
    
    // Get current customer status (in case it changed since initialization)
    const currentStatus = this.getCurrentCustomerStatus();
    this.customerStatus = currentStatus;
    console.log('👤 Customer status:', this.customerStatus);
    
    // If logged-in active member, never show popup
    if (this.customerStatus.isLoggedIn && this.customerStatus.isActiveMember) {
      console.log('Active member detected - skipping popup and removing membership plan if present');
      // Also hide cart membership panel if present
      try {
        document.querySelectorAll('.cart-membership-offer, #cart-membership-offer, [data-cart-membership-offer]').forEach(el => {
          el.style.display = 'none';
        });
      } catch (e) {
        console.warn('Failed to hide cart membership offer panel:', e);
      }
      
      // Check if membership plan is in cart and remove it
      await this.removeMembershipPlanForActiveMember();
      return;
    }
    
    try {
      console.log('📡 Fetching cart data...');
      const cartResponse = await fetch('/cart.js');
      
      if (!cartResponse.ok) {
        throw new Error(`Cart fetch failed: ${cartResponse.status}`);
      }
      
      const cart = await cartResponse.json();
      console.log('✅ Cart data fetched successfully');
      
      console.log('🛒 Cart items:', cart.items.length, 'items');
      console.log('🛒 Cart details:', cart.items.map(item => ({
        title: item.product_title,
        variant: item.variant_title,
        quantity: item.quantity
      })));
      
      // Check if membership is already in cart
      const existingMembership = cart.items.find(item => 
        item.product_title && (
          item.product_title.toLowerCase().includes('plan') ||
          item.product_title.toLowerCase().includes('gold') ||
          item.product_title.toLowerCase().includes('platinum')
        )
      );
      
      console.log('💳 Existing membership found:', existingMembership ? existingMembership.product_title : 'NONE');
      
      // Only show popup for existing membership if we're on cart page with recent add
      // Don't show popup for manually added membership
      if (existingMembership) {
        const urlParams = new URLSearchParams(window.location.search);
        const addedToCart = urlParams.get('added');
        
        // NEW: Check if there are non-member variants that need conversion
        // This handles the case where membership is added via direct URL (e.g., /cart/update?updates[ID]=1)
        console.log('🔍 Checking for non-member variants to convert...');
        
        const nonMemberItems = cart.items.filter(item => {
          // Skip membership products
          if (item.product_title && (
            item.product_title.toLowerCase().includes('plan') ||
            item.product_title.toLowerCase().includes('gold') ||
            item.product_title.toLowerCase().includes('platinum')
          )) {
            console.log('  ⏭️  Skipping membership product:', item.product_title);
            return false;
          }
          
          // Check if this is a non-member variant
          const variantTitle = item.variant_title || '';
          const isNonMember = !variantTitle.toLowerCase().includes('member') && 
                 !variantTitle.toLowerCase().includes('beauty pie+');
          
          console.log(`  ${isNonMember ? '✅' : '❌'} ${item.product_title} - ${variantTitle} - ${isNonMember ? 'NON-MEMBER (needs conversion)' : 'already member'}`);
          
          return isNonMember;
        });
        
        console.log('📊 Found', nonMemberItems.length, 'non-member items that need conversion');
        
        // If membership exists AND there are non-member variants, convert them
        if (nonMemberItems.length > 0) {
          // Check if already converting to prevent duplicate runs
          if (this.isConvertingVariants) {
            console.log('⚠️ Conversion already in progress - skipping duplicate request');
            return;
          }
          
          // Set flag to prevent duplicate conversions
          this.isConvertingVariants = true;
          
          console.log('🔄 Starting conversion process...');
          console.log('🔄 Membership in cart with', nonMemberItems.length, 'non-member variants - converting to member prices');
          
          // Show loading indicator
          console.log('🔄 Showing loading indicator...');
          this.showMembershipLoadingIndicator('Converting to member prices...');
          
          try {
            // Convert all non-member variants to member variants
            console.log('🔄 Calling convertRegularVariantsToMember(true, true)...');
            // Pass skipRefresh=true to prevent premature reload from refreshCartSections
            await this.convertRegularVariantsToMember(true, true); // force conversion + skip refresh - WAIT for it to complete!
            
            console.log('✅ Conversion function completed!');
            
            // Hide loading indicator
            console.log('✅ Hiding loading indicator...');
            this.hideMembershipLoadingIndicator();
            
            // IMPORTANT: Only reload AFTER conversion is complete
            console.log('✅ All variants converted - reloading page in 1000ms');
            console.log('💡 TIP: Check console logs above for any errors!');
            
            // Shorter delay since conversion is already complete
            setTimeout(() => {
              console.log('🔄 Reloading now...');
              window.location.reload();
            }, 1000); // Only 1 second delay AFTER conversion completes
            
          } catch (error) {
            console.error('❌ Error during conversion:', error);
            this.hideMembershipLoadingIndicator();
            this.isConvertingVariants = false; // Reset flag on error
            
            // Still reload on error to show current state
            setTimeout(() => {
              console.log('⚠️ Reloading after error...');
              window.location.reload();
            }, 2000);
          }
          
          return;
        } else {
          console.log('ℹ️  No non-member variants to convert');
        }
        
        // No popup logic - conversion done, just return
        console.log('✅ Membership handling complete');
        return;
      }
      
      // Check if we have non-member variants
      const nonMemberItems = cart.items.filter(item => {
        // Skip membership products
        if (item.product_title && (
          item.product_title.toLowerCase().includes('plan') ||
          item.product_title.toLowerCase().includes('gold') ||
          item.product_title.toLowerCase().includes('platinum')
        )) {
          return false;
        }
        
        // Check if this is a non-member variant
        const variantTitle = item.variant_title || '';
        return !variantTitle.toLowerCase().includes('member') && 
               !variantTitle.toLowerCase().includes('beauty pie+');
      });
      
      console.log('Non-member items found:', nonMemberItems);
      
      if (nonMemberItems.length > 0) {
        console.log('No membership in cart, adding membership and converting variants');
        
        // This will handle loading indicators and cart updates automatically
        await this.ensureMembershipProductInCart('Platinum');
      } else {
        console.log('No non-member items found, but checking if we should show popup for existing membership');
        // Even if no non-member items, show popup if membership exists
        if (existingMembership) {
          console.log('Membership exists, showing popup');
          this.showMembershipPopup();
        } else {
          // Check if we have member variants but no membership plan
          const memberItems = cart.items.filter(item => {
            // Skip membership products
            if (item.product_title && (
              item.product_title.toLowerCase().includes('plan') ||
              item.product_title.toLowerCase().includes('gold') ||
              item.product_title.toLowerCase().includes('platinum')
            )) {
              return false;
            }
            
            // Check if this is a member variant
            const variantTitle = item.variant_title || '';
            return variantTitle.toLowerCase().includes('member') || 
                   variantTitle.toLowerCase().includes('beauty pie+');
          });
          
          console.log('Member items found:', memberItems);
          
          if (memberItems.length > 0) {
            console.log('Member variants in cart but no membership plan - adding membership');
            
            // This will handle loading indicators and cart updates automatically
            await this.ensureMembershipProductInCart('Platinum', { force: true });
          }
        }
      }
      
    } catch (error) {
      console.error('❌ Error checking cart page for popup:', error);
      console.error('❌ Error stack:', error.stack);
      // Reset conversion flag on error
      this.isConvertingVariants = false;
    }
  }



  /**
   * Initialize membership products from theme settings
   */
  initializeMembershipProducts() {
    try {
      console.log('Initializing membership products from theme settings');
      
      // Get membership products from theme settings
      const goldProduct = window.themeSettings?.gold_membership_product;
      const platinumProduct = window.themeSettings?.platinum_membership_product;
      
      // Initialize Gold membership product
      if (goldProduct && goldProduct.id) {
        this.membershipProducts.gold.productId = parseInt(goldProduct.id);
        
        // Get variant ID from selected variant or first available variant
        if (goldProduct.selected_or_first_available_variant && goldProduct.selected_or_first_available_variant.id) {
          this.membershipProducts.gold.variantId = parseInt(goldProduct.selected_or_first_available_variant.id);
          console.log('Gold membership product initialized:', this.membershipProducts.gold);
        } else {
          console.warn('Gold membership product has no valid variant ID');
          this.membershipProducts.gold.productId = null; // Reset if no variant
        }
      } else {
        console.warn('Gold membership product not configured in theme settings');
      }
      
      // Initialize Platinum membership product
      if (platinumProduct && platinumProduct.id) {
        this.membershipProducts.platinum.productId = parseInt(platinumProduct.id);
        
        // Get variant ID from selected variant or first available variant
        if (platinumProduct.selected_or_first_available_variant && platinumProduct.selected_or_first_available_variant.id) {
          this.membershipProducts.platinum.variantId = parseInt(platinumProduct.selected_or_first_available_variant.id);
          console.log('Platinum membership product initialized:', this.membershipProducts.gold);
        } else {
          console.warn('Platinum membership product has no valid variant ID');
          this.membershipProducts.platinum.productId = null; // Reset if no variant
        }
      } else {
        console.warn('Platinum membership product not configured in theme settings');
      }
      
      // No fallback values - only use what's configured in theme settings
      if (!this.membershipProducts.gold.productId && !this.membershipProducts.platinum.productId) {
        console.warn('No valid membership products configured in theme settings');
        console.log('Both products must have valid IDs and variant IDs to function');
      }
      
      console.log('Final membership products configuration:', this.membershipProducts);
      
    } catch (error) {
      console.error('Error initializing membership products:', error);
    }
  }

  /**
   * Convert regular variants to member variants.
   * Processes one line at a time (like convertMemberVariantsToRegular) so multi-item conversion
   * completes reliably and we don't double qty or leave items unconverted.
   * @param {boolean} forceConversion - Force conversion even for non-active members
   * @param {boolean} skipRefresh - Skip cart refresh (useful when page will reload anyway)
   */
  async convertRegularVariantsToMember(forceConversion = false, skipRefresh = false) {
    try {
      if (!forceConversion) {
        const currentStatus = this.getCurrentCustomerStatus();
        if (!currentStatus.isLoggedIn || !currentStatus.isActiveMember) {
          this.log('User is not an active member, skipping variant conversion');
          return;
        }
      }

      this.log('Converting regular variants to member variants' + (skipRefresh ? ' (skipping refresh)' : ''));
      const maxPasses = 20;
      let convertedCount = 0;
      for (let pass = 0; pass < maxPasses; pass++) {
        const cartRes = await fetch('/cart.js?_' + Date.now());
        if (!cartRes.ok) break;
        const cart = await cartRes.json();
        const regularItems = cart.items.filter(item => {
          const variantTitle = (item.variant_title || '').toLowerCase();
          const productTitle = (item.product_title || '').toLowerCase();
          return !variantTitle.includes('member') &&
                 !variantTitle.includes('beauty pie+') &&
                 !productTitle.includes('plan') &&
                 !productTitle.includes('gold') &&
                 !productTitle.includes('platinum');
        });
        if (regularItems.length === 0) {
          this.log('No more regular variants to convert');
          break;
        }
        const one = regularItems[0];
        this.log('Converting regular to member:', one.title, 'key=', one.key);
        const productRes = await fetch(`/products/${one.handle}.js`);
        if (!productRes.ok) {
          this.log('Could not fetch product for', one.handle);
          break;
        }
        const product = await productRes.json();
        const memberVariant = product.variants && product.variants.find(v => {
          const t = (v.title || '').toLowerCase();
          return t.includes('member') || t.includes('beauty pie+');
        });
        if (!memberVariant) {
          this.log('No member variant for', one.title);
          break;
        }
        const removeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: one.key, quantity: 0 })
        });
        if (!removeRes.ok) {
          console.error('Regular variant remove failed for key', one.key);
          await new Promise(r => setTimeout(r, 1200));
          continue;
        }
        await new Promise(r => setTimeout(r, 700));
        const cartAfterRes = await fetch('/cart.js?_' + Date.now());
        if (cartAfterRes.ok) {
          const cartAfter = await cartAfterRes.json();
          const alreadyHasMember = cartAfter.items.some(
            (item) => String(item.variant_id) === String(memberVariant.id)
          );
          if (alreadyHasMember) {
            this.log('Cart already has member variant for', one.handle, '- skip add');
            convertedCount++;
            await new Promise(r => setTimeout(r, 600));
            continue;
          }
        }
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: memberVariant.id, quantity: one.quantity }]
          })
        });
        if (!addRes.ok) {
          console.error('Member variant add failed for', one.handle);
          break;
        }
        convertedCount++;
        this.updateMembershipLoadingMessage(`Converting to member prices (${convertedCount} done)...`);
        this.log('Converted', convertedCount, 'regular variant(s) to member so far');
        await new Promise(r => setTimeout(r, 1100));
      }
      this.log('Finished converting', convertedCount, 'regular variants to member variants');
      document.dispatchEvent(new CustomEvent('membership:variants-converted', { detail: { convertedCount } }));
      if (!skipRefresh && convertedCount > 0) {
        await this.refreshCartSections();
      }
    } catch (error) {
      console.error('Error converting regular variants:', error);
    }
  }

  /**
   * Convert all member variants in cart to regular (non-member) variants.
   * Used when user removes membership so cart has no member variants and we don't re-add membership.
   * Processes one line at a time with longer delays so cart updates are visible before next fetch.
   * @param {boolean} skipRefresh - Skip cart refresh when page will reload
   */
  async convertMemberVariantsToRegular(skipRefresh = false) {
    this.isConvertingVariants = true;
    try {
      this.log('Converting member variants to regular variants' + (skipRefresh ? ' (skipping refresh)' : ''));
      const maxPasses = 20;
      let convertedCount = 0;
      for (let pass = 0; pass < maxPasses; pass++) {
        const cartRes = await fetch('/cart.js?_' + Date.now());
        if (!cartRes.ok) break;
        const cart = await cartRes.json();
        const memberItems = cart.items.filter(item => {
          const productTitle = (item.product_title || '').toLowerCase();
          if (productTitle.includes('plan') || productTitle.includes('gold') || productTitle.includes('platinum') || productTitle.includes('membership')) {
            return false;
          }
          const variantTitle = (item.variant_title || '').toLowerCase();
          return variantTitle.includes('member') || variantTitle.includes('beauty pie+');
        });
        if (memberItems.length === 0) {
          this.log('No more member variants to convert');
          break;
        }
        const one = memberItems[0];
        this.log('Converting member variant to regular:', one.title, 'key=', one.key);
        const productRes = await fetch(`/products/${one.handle}.js`);
        if (!productRes.ok) {
          this.log('Could not fetch product for', one.handle);
          break;
        }
        const product = await productRes.json();
        const regularVariant = product.variants && product.variants.find(v => {
          const t = (v.title || '').toLowerCase();
          return !t.includes('member') && !t.includes('beauty pie+');
        });
        if (!regularVariant) {
          this.log('No regular variant for', one.title);
          break;
        }
        const removeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: one.key, quantity: 0 })
        });
        if (!removeRes.ok) {
          const errBody = await removeRes.text();
          console.error('Member variant remove failed for key', one.key, removeRes.status, errBody);
          await new Promise(r => setTimeout(r, 1200));
          continue;
        }
        await new Promise(r => setTimeout(r, 700));
        const cartAfterRes = await fetch('/cart.js?_' + Date.now());
        if (cartAfterRes.ok) {
          const cartAfter = await cartAfterRes.json();
          const alreadyHasRegular = cartAfter.items.some(
            (item) => String(item.variant_id) === String(regularVariant.id)
          );
          if (alreadyHasRegular) {
            this.log('Cart already has regular variant for', one.handle, '- skip add to avoid double qty');
            convertedCount++;
            await new Promise(r => setTimeout(r, 600));
            continue;
          }
        }
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: regularVariant.id, quantity: one.quantity }]
          })
        });
        if (!addRes.ok) {
          const errBody = await addRes.text();
          console.error('Regular variant add failed for', one.handle, addRes.status, errBody);
          break;
        }
        convertedCount++;
        this.log('Converted', convertedCount, 'member variant(s) to regular so far');
        await new Promise(r => setTimeout(r, 1100));
      }
      this.log('Finished converting', convertedCount, 'member variants to regular');
      document.dispatchEvent(new CustomEvent('membership:variants-converted-to-regular', { detail: { convertedCount } }));
      if (!skipRefresh && convertedCount > 0) {
        await this.refreshCartSections();
      }
    } catch (error) {
      console.error('Error converting member variants to regular:', error);
    } finally {
      this.isConvertingVariants = false;
    }
  }

  /**
   * Update variant options visibility based on cart state
   */
  async updateVariantOptionsVisibility() {
    try {
      console.log('🔍 Updating variant options visibility...');
      
      // Get current cart
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;
      
      const cart = await cartResponse.json();
      
      // Check if cart is completely empty
      const isCartEmpty = cart.items.length === 0;
      
      // Check if membership is in cart
      const hasMembershipProduct = cart.items.some(item => {
        const productTitle = (item.product_title || '').toLowerCase();
        return productTitle.includes('plan') || 
               productTitle.includes('gold') || 
               productTitle.includes('platinum') ||
               productTitle.includes('membership');
      });

      // Treat logged-in active members like they already have membership access
      const currentStatus = this.getCurrentCustomerStatus();
      const isActiveMember = currentStatus.isLoggedIn && currentStatus.isActiveMember;
      const hasMembership = hasMembershipProduct || isActiveMember;
      
      console.log(`📊 Cart empty: ${isCartEmpty}, Membership in cart: ${hasMembershipProduct}, Active member: ${isActiveMember}`);
      
      // Find all variant option labels - try multiple selectors
      const variantLabels = document.querySelectorAll(
        'label[class*="value-option"], .popover__value-option, label[for*="option"], fieldset.variant-picker__option label'
      );
      
      console.log(`📊 Found ${variantLabels.length} variant option elements`);
      
      let hiddenCount = 0;
      let shownCount = 0;
      
      variantLabels.forEach(label => {
        const text = label.textContent.toLowerCase();
        const input = label.querySelector('input') || document.getElementById(label.getAttribute('for'));
        
        console.log(`🔍 Checking: "${text.trim().substring(0, 30)}..."`);
        
        // Check if this is a MEMBER variant
        const isMemberVariant = text.includes('member') || text.includes('beauty pie+');
        
        // Check if this is a NON-MEMBER variant (shop now, regular price, etc.)
        const isNonMemberVariant = !isMemberVariant && 
                                   (text.includes('shop now') || 
                                    text.includes('regular') || 
                                    text.includes('non-member') || 
                                    text.includes('standard') ||
                                    text.includes('non member'));
        
        // LOGIC:
        // 1. If cart is empty, show ALL variants (both member and non-member)
        // 2. If membership exists, hide non-member variants, show member variants
        // 3. If no membership, show all variants
        
        if (isCartEmpty) {
          // Cart is empty - show ALL variants
          label.style.display = '';
          if (input) input.disabled = false;
          shownCount++;
          console.log(`  ✅ Showing (cart empty): "${text.trim().substring(0, 30)}..."`);
        } else if (hasMembership && isNonMemberVariant) {
          // Membership exists - hide non-member variants
          label.style.display = 'none';
          if (input) input.disabled = true;
          hiddenCount++;
          console.log(`  ❌ Hiding (membership active): "${text.trim().substring(0, 30)}..."`);
        } else {
          // Show this variant (either no membership or this is a member variant)
          label.style.display = '';
          if (input) input.disabled = false;
          shownCount++;
          console.log(`  ✅ Showing: "${text.trim().substring(0, 30)}..."`);
        }
      });
      
      console.log(`✅ Variant update complete: ${hiddenCount} hidden, ${shownCount} shown`);
    } catch (error) {
      console.error('Error updating variant options:', error);
    }
  }

  cleanup() {
    console.log('Cleaning up MembershipCartManager resources');
    
    // Clean up cart observer
    if (this.cartObserver) {
      this.cartObserver.disconnect();
      this.cartObserver = null;
    }
    
    // Clear any pending timeouts
    if (this.cartCheckTimeout) {
      clearTimeout(this.cartCheckTimeout);
      this.cartCheckTimeout = null;
    }
    
    // Clear auto-removal interval
    if (this.autoRemovalInterval) {
      clearInterval(this.autoRemovalInterval);
      this.autoRemovalInterval = null;
    }

    if (this.membershipReloadTimeout) {
      clearTimeout(this.membershipReloadTimeout);
      this.membershipReloadTimeout = null;
      this.pendingReloadReason = null;
    }
  }
}

// Add CSS styles for membership loading overlay
const membershipLoadingStyles = `
  <style>
    .membership-loading-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    
    .membership-loading-content {
      background: white;
      padding: 2rem;
      border-radius: 12px;
      text-align: center;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      max-width: 300px;
      width: 90%;
    }
    
    .membership-loading-spinner {
      width: 40px;
      height: 40px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #007bff;
      border-radius: 50%;
      animation: membership-spin 1s linear infinite;
      margin: 0 auto 1rem;
    }
    
    .membership-loading-text {
      color: #333;
      font-size: 1rem;
      font-weight: 500;
      margin: 0;
      line-height: 1.4;
    }
    
    @keyframes membership-spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    @media (max-width: 768px) {
      .membership-loading-content {
        padding: 1.5rem;
        margin: 1rem;
      }
      
      .membership-loading-text {
        font-size: 0.9rem;
      }
    }
  </style>
`;

// Inject styles into the page
if (!document.querySelector('#membership-loading-styles')) {
  const styleElement = document.createElement('div');
  styleElement.id = 'membership-loading-styles';
  styleElement.innerHTML = membershipLoadingStyles;
  document.head.appendChild(styleElement);
}

// Initialize the membership cart manager
document.addEventListener('DOMContentLoaded', function() {
  // Clear any old cookies that might interfere
  const cookies = document.cookie.split(';');
  const hasOldCookie = cookies.some(cookie => {
    const [name] = cookie.trim().split('=');
    return name === 'membership_auto_add_suppressed';
  });
  
  if (hasOldCookie) {
    console.log('🧹 Clearing old membership_auto_add_suppressed cookie on page load');
    document.cookie = 'membership_auto_add_suppressed=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }
  
  // Check if popup element exists
  const popup = document.getElementById('membership-popup');
  if (popup) {
    console.log('Membership popup element found in DOM');
    } else {
    console.error('Membership popup element NOT found in DOM');
  }
  
  // Only create one instance
  if (!window.membershipCartManager) {
    window.membershipCartManager = new MembershipCartManager();
  }

     // Handle benefits toggle functionality with event delegation
    document.addEventListener('click', function (e) {
      if (e.target.closest('.benefits-toggle')) {
        const toggle = e.target.closest('.benefits-toggle');
        const action = toggle.getAttribute('data-action');
        const benefitsList = toggle
          .closest('.cart-membership-offer__details')
          .querySelector('.cart-membership-offer__benefits');

        if (action === 'read-more') {
          // Show all benefits
          benefitsList.querySelectorAll('.benefit-item--hidden').forEach((benefit) => {
            benefit.style.display = 'list-item';
            benefit.classList.remove('benefit-item--hidden');
            benefit.classList.add('benefit-item--visible');
          });

          // Update toggle button
          toggle.setAttribute('data-action', 'read-less');
          toggle.querySelector('.benefits-toggle__text').textContent = 'Read less';
          toggle.querySelector('.benefits-toggle__icon').textContent = '▲';
        } else if (action === 'read-less') {
          // Hide benefits beyond the initial count
          const showCount = parseInt(benefitsList.getAttribute('data-show-benefits'));
          const allBenefits = benefitsList.querySelectorAll('.benefit-item');

          allBenefits.forEach((benefit, index) => {
            if (index >= showCount) {
              benefit.style.display = 'none';
              benefit.classList.remove('benefit-item--visible');
              benefit.classList.add('benefit-item--hidden');
            } else {
              // Ensure first few benefits are visible
              benefit.style.display = 'list-item';
              benefit.classList.remove('benefit-item--hidden');
              benefit.classList.add('benefit-item--visible');
            }
          });

          // Update toggle button
          toggle.setAttribute('data-action', 'read-more');
          toggle.querySelector('.benefits-toggle__text').textContent = 'Read more';
          toggle.querySelector('.benefits-toggle__icon').textContent = '▼';
        }
      }
    });
    
});

// Expose global functions for external use
window.checkForEmptyCartAndCleanupCookies = MembershipCartManager.checkForEmptyCartAndCleanupCookies;

// Expose conversion functions for cart drawer compact version
window.convertRegularVariantsToMemberGlobal = async function(forceConversion = false) {
  if (window.membershipCartManager) {
    console.log('🔄 Converting regular variants to member variants via global function');
    return await window.membershipCartManager.convertRegularVariantsToMember(forceConversion);
  } else {
    console.error('MembershipCartManager not found');
  }
};

window.convertMemberVariantsToRegularGlobal = async function() {
  // This function is provided by cart-membership-offer.liquid, not membership-cart.js
  if (window.convertMemberVariantsToRegular) {
    console.log('🔄 Converting member variants to regular variants via cart-membership-offer function');
    return await window.convertMemberVariantsToRegular();
  } else {
    console.error('convertMemberVariantsToRegular function not found - make sure cart-membership-offer.liquid is loaded');
  }
};

// Global function for manual testing of auto-removal
window.testAutoRemoval = function() {
  // console.log('🧪 Testing auto-removal functionality...');
  if (window.membershipCartManager) {
    window.membershipCartManager.manualCheckForAutoRemoval();
  } else {
    console.error('MembershipCartManager not found');
  }
};

// Global function to check current cart state
window.checkCartState = async function() {
  // console.log('🔍 Checking current cart state...');
  try {
    const cartResponse = await fetch('/cart.js');
    if (cartResponse.ok) {
      const cart = await cartResponse.json();
      console.log('📊 Current cart:', cart);
      
      const nonMembershipItems = cart.items.filter(item => {
        const productTitle = (item.product_title || '').toLowerCase();
        return !productTitle.includes('plan') && 
               !productTitle.includes('gold') && 
               !productTitle.includes('platinum') &&
               !productTitle.includes('membership');
      });
      
      const memberVariants = cart.items.filter(item => {
        const variantTitle = (item.variant_title || '').toLowerCase();
        return variantTitle.includes('member') || 
               variantTitle.includes('beauty pie+');
      });
      
      const existingMembership = cart.items.filter(item => {
        const productTitle = (item.product_title || '').toLowerCase();
        return productTitle.includes('plan') || 
               productTitle.includes('gold') || 
               productTitle.includes('platinum') ||
               productTitle.includes('membership');
      });
      
      // console.log(`📊 Non-membership items: ${nonMembershipItems.length}`);
      // console.log(`📊 Member variants: ${memberVariants.length}`);
      // console.log(`📊 Existing membership: ${existingMembership.length}`);
      // console.log(`📊 Total items: ${cart.items.length}`);
      
      if (nonMembershipItems.length === 0 && cart.items.length > 0) {
        console.log('🚨 SHOULD TRIGGER AUTO-REMOVAL');
      } else if (memberVariants.length > 0 && existingMembership.length === 0) {
        console.log('🚨 SHOULD ADD MEMBERSHIP PLAN');
      } else {
        console.log('✅ No auto-removal or membership addition needed');
      }
    }
  } catch (error) {
    console.error('Error checking cart state:', error);
  }
};

// Global function to test member variants without membership scenario
window.testMemberVariantsWithoutMembership = function() {
  console.log('🧪 Testing member variants without membership scenario...');
  if (window.membershipCartManager) {
    window.membershipCartManager.checkForMemberVariantsWithoutMembership();
  } else {
    console.error('MembershipCartManager not found');
  }
};



// Global function to check all cookies (for debugging)
window.checkAllCookies = function() {
  console.log('🍪 All current cookies:');
  const cookies = document.cookie.split(';');
  cookies.forEach(cookie => {
    const [name, value] = cookie.trim().split('=');
    console.log(`  ${name} = ${value}`);
  });
  
  // Check specifically for old cookie
  const hasOldCookie = cookies.some(cookie => {
    const [name] = cookie.trim().split('=');
    return name === 'membership_auto_add_suppressed';
  });
  
  if (hasOldCookie) {
    console.error('❌ OLD COOKIE FOUND: membership_auto_add_suppressed');
  } else {
    console.log('✅ No old cookie found');
  }
};

// Global function to force show popup (for testing)
window.forceShowMembershipPopup = function() {
  if (window.membershipCartManager) {
    console.log('🎯 Forcing membership popup to show');
    window.membershipCartManager.showMembershipPopup();
  } else {
    console.error('Membership cart manager not available');
  }
};

// Global function to test cart page popup logic (for testing)
window.testCartPagePopup = function() {
  if (window.membershipCartManager) {
    console.log('🧪 Testing cart page popup logic');
    window.membershipCartManager.checkCartPageForPopup();
  } else {
    console.error('Membership cart manager not available');
  }
};

// Global function to show popup for existing membership (for testing)
window.showPopupForExistingMembership = function() {
  if (window.membershipCartManager) {
    console.log('🎯 Showing popup for existing membership');
    window.membershipCartManager.showMembershipPopup();
  } else {
    console.error('Membership cart manager not available');
  }
};

// Global function to simulate recent add to cart (for testing)
window.simulateRecentAdd = function() {
  // Add ?added=true to URL to simulate recent add
  const currentUrl = new URL(window.location);
  currentUrl.searchParams.set('added', 'true');
  window.history.replaceState({}, '', currentUrl);
  console.log('🔧 Simulated recent add to cart');
  
  // Trigger cart page popup check
  if (window.membershipCartManager) {
    window.membershipCartManager.checkCartPageForPopup();
  }
};

// Global function to test case 2 (logged in but not active member)
window.testCase2 = function() {
  console.log('🧪 Testing Case 2: Logged in but not active member');
  
  if (window.membershipCartManager) {
    console.log('Customer status:', window.membershipCartManager.customerStatus);
    
    // Simulate adding a member variant to cart
    console.log('Simulating member variant addition...');
    window.membershipCartManager.handleMemberVariantAdd([
      {
        title: 'Test Product',
        variant_title: 'Member Price',
        handle: 'test-product'
      }
    ]);
  } else {
    console.error('Membership cart manager not available');
  }
};

// Global function to test case 2 with non-member variant
window.testCase2NonMember = function() {
  console.log('🧪 Testing Case 2: Logged in but not active member with non-member variant');
  
  if (window.membershipCartManager) {
    console.log('Customer status:', window.membershipCartManager.customerStatus);
    
    // Simulate adding a non-member variant to cart
    console.log('Simulating non-member variant addition...');
    window.membershipCartManager.handleNonMemberVariantAdd([
      {
        title: 'Test Product',
        variant_title: 'Regular Price',
        handle: 'test-product'
      }
    ]);
  } else {
    console.error('Membership cart manager not available');
  }
};

// Global function to test active member login scenario
window.testActiveMemberLogin = function() {
  console.log('🧪 Testing Active Member Login Scenario');
  
  if (window.membershipCartManager) {
    console.log('Current customer status:', window.membershipCartManager.customerStatus);
    
    // Simulate user logging in as active member
    console.log('Simulating login as active member...');
    window.membershipCartManager.checkLoginStatusAndUpdateCart();
  } else {
    console.error('Membership cart manager not available');
  }
};

// Global function to manually remove membership plan for active member
window.removeMembershipPlanForActiveMember = function() {
  console.log('🧪 Manually removing membership plan for active member');
  
  if (window.membershipCartManager) {
    window.membershipCartManager.removeMembershipPlanForActiveMember();
  } else {
    console.error('Membership cart manager not available');
  }
};
